<template>
  <v-app id="inspire" class="btp-app">
    <v-app-bar app v-if="showChrome" height="56" class="btp-topbar" flat>
      <v-app-bar-nav-icon @click="claimsMobile ? drawer = !drawer : miniDrawer = !miniDrawer" class="white--text"></v-app-bar-nav-icon>
      <v-btn v-if="!claimsMobile" icon @click="goBack" class="ml-2 white--text">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <v-toolbar-title class="white--text font-weight-bold">{{ currentPageTitle }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <GlobalSearch v-if="!claimsMobile" class="mr-1" />
      <v-btn v-if="!claimsMobile" icon small @click="toggleLocale" class="mr-2 white--text">
        <span class="text-caption font-weight-bold" style="font-size: 11px !important;">{{ $i18n.locale === 'pl' ? 'EN' : 'PL' }}</span>
      </v-btn>
      <v-menu v-if="!claimsMobile" offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            outlined
            small
            dark
            class="mr-3 white--text text-none"
            style="border-color: rgba(255,255,255,0.3);"
            v-bind="attrs"
            v-on="on"
            :loading="switchingCompany"
          >
            <v-icon left small>mdi-office-building</v-icon>
            {{ activeCompanyName || 'Wybierz firmę' }}
            <v-icon right small>mdi-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list dense>
          <v-list-item
            v-for="c in availableCompanies"
            :key="c.id"
            @click="switchCompany(c)"
            :disabled="c.id === activeCompanyId"
          >
            <v-list-item-icon class="mr-3">
              <v-icon small :color="c.id === activeCompanyId ? 'green' : 'grey'">
                {{ c.id === activeCompanyId ? 'mdi-check-circle' : 'mdi-office-building-outline' }}
              </v-icon>
            </v-list-item-icon>
            <v-list-item-title>{{ c.name }}</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="availableCompanies.length === 0" disabled>
            <v-list-item-title class="grey--text">Brak firm</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-chip
        v-if="isHeadAdmin && !claimsMobile"
        small
        class="mr-2 font-weight-black"
        color="amber darken-2"
        text-color="white"
        title="HEAD ADMIN — pełen dostęp, konto chronione"
      >
        <v-icon left small>mdi-shield-crown</v-icon>
        HEAD
      </v-chip>
      <v-chip v-if="!claimsMobile" small outlined dark class="mr-3 white--text" style="border-color: rgba(255,255,255,0.3);">
        BTP v2.0.3
      </v-chip>
      <v-menu
        v-model="notificationsMenu"
        :close-on-content-click="false"
        offset-y
        max-width="400"
        class="mr-2"
      >
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            icon
            v-bind="attrs"
            v-on="on"
            @click="openNotificationsPreview"
            class="mr-2 white--text"
          >
            <v-badge
              :content="unreadNotificationsCount"
              :value="unreadNotificationsCount > 0"
              color="red"
              overlap
            >
              <v-icon>mdi-bell</v-icon>
            </v-badge>
          </v-btn>
        </template>

        <v-card class="btp-notification-card">
          <v-card-title class="pa-3">
            <span>Powiadomienia</span>
            <v-spacer></v-spacer>
            <v-btn
              v-if="unreadNotificationsCount > 0"
              small
              text
              color="primary"
              :loading="markingAllRead"
              @click="markAllNotificationsRead"
            >
              Oznacz wszystkie
            </v-btn>
            <v-btn
              small
              text
              color="accent"
              @click="showAllNotifications"
            >
              Zobacz wszystkie
            </v-btn>
          </v-card-title>

          <v-divider></v-divider>

          <v-card-text class="pa-0" style="max-height: 300px; overflow-y: auto;">
            <div v-if="notifications.length === 0" class="text-center pa-4">
              <v-icon large color="grey">mdi-bell-off</v-icon>
              <p class="grey--text mt-2">Brak powiadomień</p>
            </div>

            <v-list v-else dense class="btp-notification-list">
              <v-list-item
                v-for="notification in getLastNotifications(5)"
                :key="notification.id"
                @click="markAsReadAndNavigate(notification)"
                :class="{ 'btp-unread': !notification.read }"
              >
                <v-list-item-icon>
                  <v-icon :color="getNotificationColor(notification.type)">
                    {{ getNotificationIcon(notification.type) }}
                  </v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title class="text-wrap">
                    {{ notification.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle class="text-wrap">
                    {{ notification.message }}
                  </v-list-item-subtitle>
                  <v-list-item-subtitle class="caption">
                    {{ formatTime(notification.timestamp) }}
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action v-if="!notification.read">
                  <v-chip x-small color="red" text-color="white">
                    Nowe
                  </v-chip>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-menu>
      <v-btn v-if="!claimsMobile" icon class="mr-1 white--text" @click="$root.$emit('open-chat')" title="Czat">
        <v-badge
          :content="chatUnreadCount"
          :value="chatUnreadCount > 0"
          color="red"
          overlap
        >
          <v-icon>mdi-message-text</v-icon>
        </v-badge>
      </v-btn>
      <v-menu offset-y left min-width="260">
        <template v-slot:activator="{ on, attrs }">
          <v-btn icon class="white--text" v-bind="attrs" v-on="on">
            <v-avatar v-if="myProfile.avatar" size="32">
              <img :src="myProfile.avatar" alt="avatar" />
            </v-avatar>
            <v-icon v-else>mdi-account-circle</v-icon>
          </v-btn>
        </template>
        <v-card>
          <div class="d-flex align-center pa-3">
            <v-avatar size="44" color="grey lighten-2" class="mr-3">
              <img v-if="myProfile.avatar" :src="myProfile.avatar" alt="avatar" />
              <v-icon v-else color="grey darken-1">mdi-account</v-icon>
            </v-avatar>
            <div style="min-width:0;">
              <div class="font-weight-bold text-truncate" style="max-width:170px;">
                {{ (myProfile.firstName + ' ' + myProfile.lastName).trim() || myProfile.email || 'Konto' }}
              </div>
              <div class="caption grey--text text-truncate" style="max-width:170px;">
                {{ roleLabel(myProfile.role) }}<span v-if="activeCompanyName"> · {{ activeCompanyName }}</span>
              </div>
            </div>
          </div>
          <v-divider></v-divider>
          <v-list dense>
            <v-list-item @click="goToAccountSettings()">
              <v-list-item-icon class="mr-3">
                <v-icon>mdi-account-cog</v-icon>
              </v-list-item-icon>
              <v-list-item-title>{{ $t('app.accountSettings') }}</v-list-item-title>
            </v-list-item>
            <v-divider></v-divider>
            <v-list-item @click="logout()">
              <v-list-item-icon class="mr-3">
                <v-icon color="red">mdi-logout</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="red--text">{{ $t('app.logout') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-menu>
      <v-progress-linear
        :active="loading"
        :indeterminate="loading"
        absolute
        bottom
        style="z-index: 9999"
        color="accent"
      ></v-progress-linear>
    </v-app-bar>
    <v-overlay :value="overlay" style="z-index: 9999"></v-overlay>

    <v-navigation-drawer
      v-model="drawer"
      :mini-variant="miniDrawer && !claimsMobile"
      :permanent="!claimsMobile"
      :temporary="claimsMobile"
      app
      v-if="showChrome"
      width="220"
      mini-variant-width="56"
      class="btp-sidebar"
    >
      <div
        class="btp-sidebar-logo d-flex align-center"
        :class="miniDrawer ? 'justify-center px-2' : 'justify-start pl-3'"
      >
        <img
          v-if="companyLogoUrl"
          :src="companyLogoUrl"
          alt="Logo"
          :class="miniDrawer ? 'btp-logo-img mini' : 'btp-logo-img'"
        >
        <template v-else>
          <span v-if="!miniDrawer" class="text-h5 font-weight-bold white--text">BTP</span>
          <span v-else class="text-h5 font-weight-bold white--text">B</span>
        </template>
      </div>

      <div v-if="!miniDrawer" class="px-3 pt-2 pb-1">
        <v-autocomplete
          ref="pageSearch"
          v-model="selectedPage"
          :search-input.sync="pageSearchText"
          :items="navPages"
          :label="$t('app.searchPage')"
          prepend-inner-icon="mdi-magnify"
          outlined
          dense
          hide-details
          clearable
          item-text="text"
          item-value="link"
          @change="navigateToPage"
          class="btp-nav-search"
        >
          <template v-slot:item="{ item }">
            <v-list-item-icon class="mr-3">
              <v-icon size="20">{{ item.icon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ item.text }}</v-list-item-title>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </div>

      <v-list dense nav class="btp-nav-list">
        <template v-for="group in groupedLinks">
          <!-- Standalone items (no group) -->
          <template v-if="!group.groupName">
            <v-list-item
              v-for="[icon, text, link] in group.items"
              :key="'s-' + link"
              v-bind="atrybutyPozycji(link)"
              active-class="btp-nav-active"
              class="btp-nav-item"
            >
              <v-list-item-icon>
                <v-badge v-if="link === 'chat' && chatUnreadCount > 0" :content="chatUnreadCount" color="red" overlap>
                  <v-icon size="22">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'invoices' && pendingInvoicesCount > 0" :content="pendingInvoicesCount" color="red" overlap>
                  <v-icon size="22">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'pending-drivers-changes' && pendingChangesCount > 0" :content="pendingChangesCount" color="red" overlap>
                  <v-icon size="22">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'driver-onboarding' && pendingOnboardingCount > 0" :content="pendingOnboardingCount" color="red" overlap>
                  <v-icon size="22">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'pending-documents' && pendingDocumentsCount > 0" :content="pendingDocumentsCount" color="red" overlap>
                  <v-icon size="22">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'inspections' && pendingInspectionsCount > 0" :content="pendingInspectionsCount" color="red" overlap>
                  <v-icon size="22">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'website-leads' && pendingWebsiteLeadsCount > 0" :content="pendingWebsiteLeadsCount" color="red" overlap>
                  <v-icon size="22">{{ icon }}</v-icon>
                </v-badge>
                <v-icon v-else size="22">{{ icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="btp-nav-text">{{ text }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </template>

          <!-- Grouped items -->
          <v-list-group
            v-else
            :key="group.groupName"
            :prepend-icon="group.icon"
            no-action
            class="btp-nav-group"
            :value="isGroupActive(group)"
          >
            <template v-slot:activator>
              <v-list-item-content>
                <v-list-item-title class="btp-nav-text">{{ group.groupName }}</v-list-item-title>
              </v-list-item-content>
            </template>

            <v-list-item
              v-for="[icon, text, link] in group.items"
              :key="link"
              v-bind="atrybutyPozycji(link)"
              active-class="btp-nav-active"
              class="btp-nav-item btp-nav-subitem"
            >
              <v-list-item-icon>
                <v-badge v-if="link === 'chat' && chatUnreadCount > 0" :content="chatUnreadCount" color="red" overlap>
                  <v-icon size="18">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'invoices' && pendingInvoicesCount > 0" :content="pendingInvoicesCount" color="red" overlap>
                  <v-icon size="18">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'pending-drivers-changes' && pendingChangesCount > 0" :content="pendingChangesCount" color="red" overlap>
                  <v-icon size="18">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'driver-onboarding' && pendingOnboardingCount > 0" :content="pendingOnboardingCount" color="red" overlap>
                  <v-icon size="18">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'pending-documents' && pendingDocumentsCount > 0" :content="pendingDocumentsCount" color="red" overlap>
                  <v-icon size="18">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'inspections' && pendingInspectionsCount > 0" :content="pendingInspectionsCount" color="red" overlap>
                  <v-icon size="18">{{ icon }}</v-icon>
                </v-badge>
                <v-badge v-else-if="link === 'website-leads' && pendingWebsiteLeadsCount > 0" :content="pendingWebsiteLeadsCount" color="red" overlap>
                  <v-icon size="18">{{ icon }}</v-icon>
                </v-badge>
                <v-icon v-else size="18">{{ icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="btp-nav-text">{{ text }}</v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list-group>
        </template>
      </v-list>

      <template v-slot:append>
        <div class="pa-2">
          <v-list-item v-if="canCustomizeMenu" @click="openMenuConfig()" class="btp-nav-item">
            <v-list-item-icon>
              <v-icon size="22">mdi-menu-open</v-icon>
            </v-list-item-icon>
            <v-list-item-content v-if="!miniDrawer">
              <v-list-item-title class="btp-nav-text">{{ $t('app.menuConfig') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item @click="logout()" class="btp-logout-item">
            <v-list-item-icon>
              <v-icon color="red">mdi-logout</v-icon>
            </v-list-item-icon>
            <v-list-item-content v-if="!miniDrawer">
              <v-list-item-title class="red--text font-weight-medium">{{ $t('app.logout') }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </div>
      </template>
    </v-navigation-drawer>

    <!-- Menu Configuration Dialog -->
    <v-dialog v-model="menuConfigDialog" max-width="900" scrollable content-class="menu-config-dialog">
      <v-card>
        <v-card-title>
          <v-icon left color="#5478FF">mdi-menu-open</v-icon>
          {{ $t('app.menuConfigTitle') }}
          <v-spacer></v-spacer>
          <v-btn icon @click="menuConfigDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pt-4" style="max-height: 70vh;">
          <v-row>
            <v-col cols="12">
              <div class="d-flex align-center mb-4">
                <v-text-field
                  v-model="newGroupName"
                  :label="$t('app.newGroupLabel')"
                  outlined
                  dense
                  hide-details
                  class="mr-2"
                  @keyup.enter="addMenuGroup"
                ></v-text-field>
                <v-select
                  v-model="newGroupIcon"
                  :items="availableIcons"
                  :label="$t('app.iconLabel')"
                  outlined
                  dense
                  hide-details
                  class="mr-2"
                  style="max-width: 220px;"
                >
                  <template v-slot:item="{ item }">
                    <v-icon class="mr-2" size="20">{{ item }}</v-icon>
                    {{ item.replace('mdi-', '') }}
                  </template>
                  <template v-slot:selection="{ item }">
                    <v-icon class="mr-1" size="20">{{ item }}</v-icon>
                    {{ item.replace('mdi-', '') }}
                  </template>
                </v-select>
                <v-btn color="primary" @click="addMenuGroup" :disabled="!newGroupName">
                  <v-icon left>mdi-plus</v-icon> {{ $t('app.add') }}
                </v-btn>
              </div>
            </v-col>
          </v-row>

          <!-- Unassigned pages -->
          <v-card
            outlined
            class="mb-4"
            v-if="unassignedPages.length > 0"
            @dragover.prevent
            @dragenter.prevent="dragOverGroup = 'unassigned'"
            @dragleave.prevent="dragOverGroup = null"
            @drop="onDropToUnassigned($event)"
            :class="{ 'btp-drag-over': dragOverGroup === 'unassigned' }"
          >
            <v-card-title class="subtitle-2 py-2">
              <v-icon left small>mdi-help-circle</v-icon>
              {{ $t('app.unassignedPages') }}
            </v-card-title>
            <v-card-text class="py-1">
              <v-chip
                v-for="page in unassignedPages"
                :key="page[2]"
                class="ma-1 btp-draggable-chip"
                draggable
                @dragstart="onDragStart($event, 'page', page[2])"
                @dragend="onDragEnd"
                small
              >
                <v-icon left small>{{ page[0] }}</v-icon>
                {{ page[1] }}
              </v-chip>
            </v-card-text>
          </v-card>

          <!-- Standalone pages zone -->
          <v-card
            outlined
            class="mb-4"
            @dragover.prevent
            @dragenter.prevent="dragOverGroup = 'standalone'"
            @dragleave.prevent="dragOverGroup = null"
            @drop="onDropToStandalone($event)"
            :class="{ 'btp-drag-over': dragOverGroup === 'standalone' }"
          >
            <v-card-title class="subtitle-2 py-2">
              <v-icon left small>mdi-pin</v-icon>
              {{ $t('app.standalonePages') }}
            </v-card-title>
            <v-card-text class="py-1" style="min-height: 40px;">
              <span v-if="standalonePages.length === 0" class="text--secondary caption">{{ $t('app.standaloneDragHint') }}</span>
              <v-chip
                v-for="pageSlug in standalonePages"
                :key="pageSlug"
                class="ma-1 btp-draggable-chip"
                small
                close
                draggable
                @click:close="removeStandalonePage(pageSlug)"
                @dragstart.stop="onDragStart($event, 'page', pageSlug, 'standalone')"
                @dragend="onDragEnd"
              >
                <v-icon left small>{{ getPageIcon(pageSlug) }}</v-icon>
                {{ getPageName(pageSlug) }}
              </v-chip>
            </v-card-text>
          </v-card>

          <!-- Groups (only named groups, standalone handled above) -->
          <template v-for="(group, gIdx) in menuConfigGroups">
          <div
            v-if="group.groupName"
            :key="gIdx"
            class="mb-3"
            draggable
            @dragstart.self="onDragStart($event, 'group', gIdx)"
            @dragend="onDragEnd"
            @dragover.prevent
            @dragenter.prevent="onGroupDragEnter(gIdx)"
            @dragleave.prevent
            @drop="onDropToGroup($event, gIdx)"
          >
            <v-card
              outlined
              class="btp-config-group-card"
              :class="{ 'btp-drag-over': dragOverGroup === gIdx, 'btp-dragging': dragType === 'group' && dragSourceGroup === gIdx }"
            >
              <v-card-title
                class="py-2 d-flex align-center"
                style="cursor: grab;"
              >
                <v-icon left class="mr-1" size="16" style="opacity: 0.4;">mdi-drag</v-icon>
                <v-icon left>{{ group.icon }}</v-icon>
                <template v-if="editingGroupIdx !== gIdx">
                  <span class="subtitle-1 font-weight-medium">{{ group.groupName || $t('app.standalone') }}</span>
                  <v-spacer></v-spacer>
                  <v-btn icon x-small @click.stop="startEditGroup(gIdx)" v-if="group.groupName">
                    <v-icon small>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon x-small color="red" @click.stop="removeMenuGroup(gIdx)" v-if="group.groupName">
                    <v-icon small>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <template v-else>
                  <v-text-field
                    v-model="editingGroupName"
                    dense
                    hide-details
                    class="mx-2"
                    style="max-width: 200px;"
                    @keyup.enter="saveEditGroup(gIdx)"
                  ></v-text-field>
                  <v-select
                    v-model="editingGroupIcon"
                    :items="availableIcons"
                    dense
                    hide-details
                    class="mx-1"
                    style="max-width: 180px;"
                  >
                    <template v-slot:item="{ item }">
                      <v-icon class="mr-2" size="20">{{ item }}</v-icon>
                      {{ item.replace('mdi-', '') }}
                    </template>
                    <template v-slot:selection="{ item }">
                      <v-icon class="mr-1" size="20">{{ item }}</v-icon>
                    </template>
                  </v-select>
                  <v-btn icon x-small color="green" @click.stop="saveEditGroup(gIdx)">
                    <v-icon small>mdi-check</v-icon>
                  </v-btn>
                  <v-btn icon x-small @click.stop="editingGroupIdx = -1">
                    <v-icon small>mdi-close</v-icon>
                  </v-btn>
                </template>
              </v-card-title>
              <v-divider></v-divider>
              <v-card-text class="py-2 btp-page-drop-zone" style="min-height: 40px;">
                <span v-if="group.pages.length === 0" class="text--secondary caption">{{ $t('app.dragHint') }}</span>
                <span
                  v-for="(pageSlug, pIdx) in group.pages"
                  :key="pageSlug"
                  class="d-inline-block"
                  @dragover.prevent.stop="onPageDragOver($event, gIdx, pIdx)"
                  @drop.stop="onDropPageAt($event, gIdx, pIdx)"
                >
                  <v-chip
                    class="ma-1 btp-draggable-chip"
                    :class="{ 'btp-page-drag-left': dragOverPage && dragOverPage.group === gIdx && dragOverPage.index === pIdx }"
                    small
                    close
                    draggable
                    @click:close="removePageFromGroup(gIdx, pageSlug)"
                    @dragstart.stop="onDragStart($event, 'page', pageSlug, gIdx)"
                    @dragend="onDragEnd"
                  >
                    <v-icon left small>{{ getPageIcon(pageSlug) }}</v-icon>
                    {{ getPageName(pageSlug) }}
                  </v-chip>
                </span>
                <!-- Drop zone at end of list -->
                <span
                  class="btp-end-drop-zone d-inline-block"
                  :style="{ width: group.pages.length === 0 ? '100%' : '40px', height: '32px', verticalAlign: 'middle' }"
                  @dragover.prevent.stop="onPageDragOver($event, gIdx, group.pages.length)"
                  @drop.stop="onDropPageAt($event, gIdx, group.pages.length)"
                >
                  <span v-if="dragType === 'page' && dragOverPage && dragOverPage.group === gIdx && dragOverPage.index === group.pages.length" class="btp-insert-indicator"></span>
                </span>
              </v-card-text>
            </v-card>
          </div>
          </template>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-btn text @click="resetMenuConfig" color="orange">
            <v-icon left>mdi-restore</v-icon>
            {{ $t('app.restoreDefaults') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn text @click="menuConfigDialog = false">{{ $t('app.cancel') }}</v-btn>
          <v-btn color="primary" @click="saveMenuConfig">
            <v-icon left>mdi-content-save</v-icon>
            {{ $t('app.save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-main class="btp-main">
      <!-- Banner podglądu konta (impersonacja head admina) -->
      <div v-if="impersonation" class="imp-banner">
        <v-icon small color="#fff" class="mr-2">mdi-account-eye</v-icon>
        <span>Tryb podglądu konta — uruchomił: <b>{{ impersonation }}</b>. Działasz jako ten użytkownik.</span>
        <v-spacer></v-spacer>
        <v-btn x-small color="white" light :loading="stoppingImp" @click="stopImpersonation">
          <v-icon x-small left>mdi-logout</v-icon>Wróć do swojego konta
        </v-btn>
      </div>
      <router-view />
    </v-main>

    <v-snackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="snackbarTimeout"
      top
      class="btp-snackbar"
    >
      <div class="d-flex align-center">
        <v-icon left color="white">{{ snackbarIcon }}</v-icon>
        <span class="btp-snackbar-text">{{ snackbarText }}</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn icon v-bind="attrs" @click="snackbar = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </template>
    </v-snackbar>
    <ChatLauncher v-if="user && $route.path !== '/chat' && !claimsMobile" />

    <!-- PWA: pasek instalacji — zawsze widoczny (dopóki nie zainstalowane/odrzucone).
         Na urządzeniach bez auto-promptu (iPad/Safari) przycisk pokazuje instrukcję. -->
    <!-- Ekran podpisu na tablecie ma pokazywać tylko to, co kierowca podpisuje —
         zachęta do instalacji panelu jest tam zbędna i myląca. -->
    <v-snackbar v-if="!isSignaturePad" v-model="showInstallPrompt" :timeout="-1" bottom left color="#1A2433" class="btp-install-snackbar">
      <div class="d-flex align-center">
        <v-icon left color="#4FC3F7">mdi-download-circle-outline</v-icon>
        <span>Zainstaluj panel BTP jako aplikację na tym urządzeniu.</span>
      </div>
      <template v-slot:action="{ attrs }">
        <v-btn color="#4FC3F7" text v-bind="attrs" @click="installApp">Zainstaluj</v-btn>
        <v-btn icon v-bind="attrs" @click="dismissInstall"><v-icon small>mdi-close</v-icon></v-btn>
      </template>
    </v-snackbar>

    <!-- Instrukcja instalacji (gdy przeglądarka nie daje przycisku, np. iPad/iPhone) -->
    <v-dialog v-model="installDialog" max-width="440">
      <v-card color="#181C28" dark>
        <v-card-title class="d-flex align-center">
          <v-icon left color="#4FC3F7">mdi-cellphone-arrow-down</v-icon>
          Zainstaluj aplikację BTP
        </v-card-title>
        <v-card-text>
          <template v-if="isIOS">
            <p>Na tym urządzeniu (Safari) dodaj aplikację ręcznie:</p>
            <ol style="line-height:1.9">
              <li>Dotknij ikony <b>Udostępnij</b> <v-icon small>mdi-export-variant</v-icon> na dolnym/górnym pasku Safari.</li>
              <li>Wybierz <b>„Do ekranu początkowego"</b>.</li>
              <li>Potwierdź <b>Dodaj</b> — ikona BTP pojawi się na ekranie.</li>
            </ol>
            <p class="text-caption grey--text mt-2">Uwaga: musi to być Safari (w Chrome na iOS tej opcji nie ma).</p>
          </template>
          <template v-else>
            <p>W menu przeglądarki wybierz jedną z opcji:</p>
            <ol style="line-height:1.9">
              <li><b>„Zainstaluj aplikację"</b> lub</li>
              <li><b>„Dodaj do ekranu głównego"</b>.</li>
            </ol>
            <p class="text-caption grey--text mt-2">Menu otworzysz ikoną <v-icon small>mdi-dots-vertical</v-icon> (Android) lub ikoną instalacji w pasku adresu (komputer).</p>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="installDialog = false">Zamknij</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Ostrzeżenie przed automatycznym wylogowaniem po bezczynności -->
    <v-dialog v-model="idleWarnDialog" max-width="420" persistent>
      <v-card>
        <v-card-title class="subtitle-1">
          <v-icon left color="warning">mdi-clock-alert-outline</v-icon>
          Jesteś tam jeszcze?
        </v-card-title>
        <v-card-text>
          Z powodu bezczynności wylogujemy Cię za
          <strong>{{ idleSecondsLeft }} s</strong>.
          Kliknij poniżej, żeby zostać zalogowanym.
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="logout()">Wyloguj mnie</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="stayLoggedIn()">Jestem tutaj</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>
<script>
const axios = require("axios");
import { installHttpCache, clearHttpCache } from './services/httpCache';
import { isSuperAdmin as isPrivilegedAdmin } from './services/permissions';
import { startIdleWatch, stopIdleWatch, markActivity } from './services/idleActivity';
import { startWorkTimeHeartbeat, stopWorkTimeHeartbeat, closeWorkSession } from './services/workTime';
import Swal from 'sweetalert2';
import { chatState, initGlobalChat, resetUnreadCount } from './plugins/chatNotifications';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
dayjs.extend(isoWeek);

// Mapowanie nazw miesięcy → numer (0-based) dla parsowania nagłówka v-date-picker.
// Vuetify 2.6 pokazuje miesiąc słownie ("styczeń 2026"), bez data-atrybutu.
const __MONTH_MAP = (() => {
  const m = {};
  ['styczeń','luty','marzec','kwiecień','maj','czerwiec','lipiec','sierpień','wrzesień','październik','listopad','grudzień']
    .forEach((name, i) => { m[name] = i; });
  ['january','february','march','april','may','june','july','august','september','october','november','december']
    .forEach((name, i) => { m[name] = i; });
  return m;
})();

function __patchIsoWeeksInPicker(picker) {
  if (!picker) return;
  const headerBtn = picker.querySelector('.v-date-picker-header__value button, .v-date-picker-header__value strong, .v-date-picker-header__value');
  const table = picker.querySelector('.v-date-picker-table--date');
  if (!headerBtn || !table) return;

  const headerText = (headerBtn.textContent || '').trim().toLowerCase();
  const yearMatch = headerText.match(/(\d{4})/);
  if (!yearMatch) return;
  const year = parseInt(yearMatch[1], 10);

  let month = -1;
  for (const key in __MONTH_MAP) {
    if (headerText.includes(key)) { month = __MONTH_MAP[key]; break; }
  }
  if (month === -1) return;

  const rows = table.querySelectorAll('tbody tr');
  rows.forEach(row => {
    const weekCell = row.querySelector('.v-date-picker-table--date__week, td:first-child small');
    if (!weekCell) return;

    // Pomiń pierwszą kolumnę (week), iteruj komórki dni
    const dayTds = Array.prototype.slice.call(row.querySelectorAll('td')).filter(td => !td.querySelector('.v-date-picker-table--date__week'));
    let firstDay = null, firstCol = 0;
    for (let i = 0; i < dayTds.length; i++) {
      const btn = dayTds[i].querySelector('button');
      const txt = btn ? (btn.textContent || '').trim() : '';
      if (txt) {
        const d = parseInt(txt, 10);
        if (!isNaN(d)) { firstDay = d; firstCol = i; break; }
      }
    }
    if (firstDay === null) return;

    // Cofnij się o `firstCol` dni żeby wyliczyć poniedziałek (kol 0 = pon przy first-day-of-week=1)
    const ref = dayjs(new Date(year, month, firstDay));
    const monday = ref.subtract(firstCol, 'day');
    const isoWk = monday.isoWeek();
    if (weekCell.textContent.trim() !== String(isoWk)) {
      weekCell.textContent = String(isoWk);
    }
  });
}

function __installIsoWeekPatcher() {
  if (window.__btpIsoWeekObserver) return;
  const observer = new MutationObserver(mutations => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!node || node.nodeType !== 1) continue;
        // Jeśli sam jest tabelą pickera lub ją zawiera
        if (node.classList && node.classList.contains('v-date-picker-table--date')) {
          const p = node.closest('.v-date-picker, .v-picker') || node.parentElement;
          __patchIsoWeeksInPicker(p);
        } else if (node.querySelector) {
          const tables = node.querySelectorAll('.v-date-picker-table--date');
          tables.forEach(t => {
            const p = t.closest('.v-date-picker, .v-picker') || t.parentElement;
            __patchIsoWeeksInPicker(p);
          });
        }
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
  window.__btpIsoWeekObserver = observer;
}
import ChatLauncher from '@/components/ChatLauncher.vue';
import GlobalSearch from '@/components/GlobalSearch.vue';

export default {
  name: "App",
  components: { ChatLauncher, GlobalSearch },
  data: () => ({
    user: localStorage.getItem("user"),
    drawer: true,
    miniDrawer: false,
    companyLogoUrl: require('@/assets/logo.png'),
    defaultLogoUrl: require('@/assets/logo.png'),
    availableCompanies: [],
    activeCompanyId: localStorage.getItem("activeCompanyId") || null,
    activeCompanyName: localStorage.getItem("activeCompanyName") || '',
    switchingCompany: false,
    stoppingImp: false,
    myProfile: { firstName: '', lastName: '', email: '', role: '', avatar: null },
    pendingInvoicesCount: 0,
    pendingOnboardingCount: 0,
    pendingDocumentsCount: 0,
    pendingChangesCount: 0,
    pendingInspectionsCount: 0,
    pendingWebsiteLeadsCount: 0,
    installPromptEvent: null,
    showInstallPrompt: false,
    installDialog: false,
    isIOS: false,
    snackbar: false,
    snackbarText: '',
    snackbarColor: '#3F51B5',
    snackbarIcon: 'mdi-check-circle',
    snackbarTimeout: 2000,
    loading: false,
    overlay: false,
    links: [],
    selectedPage: null,
    pageSearchText: null,
    notifications: [],
    unreadNotificationsCount: 0,
    markingAllRead: false,
    lastCheckedTime: null,
    notificationsMenu: false,
    // Menu config
    menuConfigDialog: false,
    idleWarnDialog: false,
    idleSecondsLeft: 60,
    menuConfigGroups: [],
    customGroupDefs: null,
    newGroupName: '',
    newGroupIcon: 'mdi-folder',
    editingGroupIdx: -1,
    editingGroupName: '',
    editingGroupIcon: '',
    // Drag & drop state
    dragType: null,
    dragPayload: null,
    dragSourceGroup: null,
    dragOverGroup: null,
    dragOverPage: null,
    availableIcons: [
      'mdi-domain', 'mdi-account', 'mdi-car-multiple', 'mdi-taxi',
      'mdi-cash-multiple', 'mdi-file-document-edit', 'mdi-import',
      'mdi-chat', 'mdi-cog', 'mdi-folder', 'mdi-chart-bar',
      'mdi-shield-account', 'mdi-truck', 'mdi-wallet', 'mdi-tools',
      'mdi-clipboard-text', 'mdi-bank', 'mdi-bell', 'mdi-calendar',
      'mdi-briefcase', 'mdi-database', 'mdi-earth', 'mdi-flag',
    ],
  }),
  computed: {
    claimsMobile() {
      return ['/claims', '/driver-map'].includes(this.$route?.path) && this.$vuetify.breakpoint.smAndDown;
    },
    // Pasek górny i menu boczne pokazujemy TYLKO realnie zalogowanym i tylko na
    // trasach wymagających auth. Gating na ważnym tokenie (reaktywnie na trasę) —
    // nie na starej fladze 'user', która zostawała po sesji i powodowała, że całe
    // menu (Flota, Kierowcy…) i WYLOGUJ widać było na ekranie /login.
    isSignaturePad() {
      return (this.$route?.path || '').startsWith('/podpis');
    },
    showChrome() {
      const token = localStorage.getItem('token');
      const authed = !!token && token !== 'null' && token !== 'undefined' && token !== '';
      const route = this.$route;
      const onAuthRoute = route ? route.matched.some((r) => r.meta && r.meta.requiresAuth) : false;
      return authed && onAuthRoute;
    },
    // Nazwa head admina, gdy działamy w trybie podglądu konta (impersonacja).
    impersonation() {
      try {
        const t = localStorage.getItem('token');
        if (!t) return null;
        const p = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        const u = p.user || p;
        return u.impersonatedBy && u.impersonatedBy.name ? u.impersonatedBy.name : null;
      } catch (e) {
        return null;
      }
    },
    chatUnreadCount() {
      return chatState.unreadCount;
    },
    currentPageTitle() {
      const route = this.$route;
      if (!route) return '';
      const match = this.links.find(([, , link]) => '/' + link === route.path);
      return match ? match[1] : '';
    },
    isAdmin() {
      return localStorage.getItem("role") !== "user";
    },
    canCustomizeMenu() {
      // Personalizacja menu dostępna dla KAŻDEGO zalogowanego admina. Konfigurator
      // i tak pokazuje wyłącznie karty, do których dany admin ma dostęp
      // (activeGroupDefs filtruje po this.links), a config jest per-user
      // (endpoint menu-config, tylko JwtAuthGuard).
      return true;
    },
    isHeadAdmin() {
      return localStorage.getItem("role") === "headadmin"
        || localStorage.getItem("actualRole") === "headadmin"
        || localStorage.getItem("isHead") === "true";
    },
    // Pozycje menu, ktore nie sa stronami panelu, tylko odsylaja na zewnatrz.
    // Statystyki uzycia (nagrania sesji, rage clicki) zbiera Microsoft Clarity —
    // trzymanie ich u siebie wymagaloby przepisywania cudzego pulpitu, wiec
    // menu po prostu otwiera oryginal w nowej karcie. Pozycja pojawia sie tylko
    // przy ustawionym VUE_APP_CLARITY_ID, tak samo jak samo nagrywanie.
    linkiZewnetrzne() {
      const clarityId = process.env.VUE_APP_CLARITY_ID;
      const mapa = {};
      if (clarityId) {
        mapa['usage-stats'] = `https://clarity.microsoft.com/projects/view/${clarityId}/dashboard`;
      }
      return mapa;
    },
    defaultGroupDefs() {
      return [
        { groupName: null, icon: null, pages: ['dashboard'] },
        { groupName: 'Firma', icon: 'mdi-domain', pages: ['company', 'subcompanies', 'bank-accounts'] },
        { groupName: 'Administracja', icon: 'mdi-account', pages: ['admins', 'hr', 'staff-activity', 'work-time'] },
        { groupName: 'Flota', icon: 'mdi-car-multiple', pages: ['fleet', 'driver-fleet', 'rent-to-own', 'crashes', 'claims', 'fuel-cards', 'inspections'] },
        { groupName: 'Kierowcy', icon: 'mdi-taxi', pages: ['drivers', 'driver-map', 'drivers-archive', 'bonuses', 'driver-onboarding', 'pending-drivers-changes'] },
        { groupName: 'Finanse', icon: 'mdi-cash-multiple', pages: ['accounting', 'invoices', 'cost-invoices', 'incomes', 'settlements', 'contracts', 'payouts', 'debtors', 'daily-payouts', 'self-service-payouts', 'bolt-sync-drivers', 'receipts', 'cash-report', 'komornik-letters', 'policy-sales'] },
        { groupName: 'Dokumenty', icon: 'mdi-file-document-edit', pages: ['document-templates', 'pending-documents', 'documents-repository', 'official-documents', 'company-licenses'] },
        { groupName: 'Kadry', icon: 'mdi-passport', pages: ['legalization'] },
        { groupName: 'Marketing', icon: 'mdi-bullhorn-variant', pages: ['marketing-posts'] },
        { groupName: 'Import', icon: 'mdi-import', pages: ['uploader', 'uploader-templates', 'import-history'] },
        { groupName: 'Integracje API', icon: 'mdi-api', pages: ['integrations'] },
        { groupName: 'Komunikacja', icon: 'mdi-chat', pages: ['assistant', 'tasks', 'notifications', 'notification-logs', 'chat', 'announcements', 'website-leads', 'sms', 'email'] },
        { groupName: 'Wykazy', icon: 'mdi-clipboard-list', pages: ['wykaz-pojazdy', 'wykaz-kierowcy'] },
        { groupName: 'Program Punktowy', icon: 'mdi-star-circle', pages: ['points-settings', 'points-referrals', 'points-reviews', 'points-reward-orders', 'points-drivers'] },
        { groupName: 'Ustawienia', icon: 'mdi-cog', pages: ['payout-config', 'wiki', 'audit-logs', 'system-settings', 'mail-config', 'app-version', 'usage-stats'] },
      ];
    },
    activeGroupDefs() {
      // Karty dostępne dla zalogowanego admina (this.links jest już filtrowane
      // po uprawnieniach). Konfigurator działa TYLKO w tym zakresie.
      const accessible = new Set(this.links.map(l => l[2]));
      const base = this.customGroupDefs ? JSON.parse(JSON.stringify(this.customGroupDefs)) : this.defaultGroupDefs;
      // Odetnij strony spoza dostępu — admin nie widzi w konfiguratorze kart,
      // do których nie ma uprawnień.
      const defs = JSON.parse(JSON.stringify(base)).map(g => ({
        ...g,
        pages: (g.pages || []).filter(p => accessible.has(p)),
      }));
      // Ensure all accessible pages from links are present - add missing to standalone
      const assigned = new Set();
      defs.forEach(g => g.pages.forEach(p => assigned.add(p)));
      const missing = this.links.map(l => l[2]).filter(slug => !assigned.has(slug));
      if (missing.length > 0) {
        let sg = defs.find(g => !g.groupName);
        if (!sg) {
          sg = { groupName: null, icon: null, pages: [] };
          defs.unshift(sg);
        }
        missing.forEach(p => sg.pages.push(p));
      }
      // Usuń grupy, które po odcięciu niedostępnych stron są puste (poza standalone).
      return defs.filter(g => !g.groupName || g.pages.length > 0);
    },
    groupedLinks() {
      const linkMap = {};
      this.links.forEach(item => {
        linkMap[item[2]] = item;
      });

      const groupTranslations = {
        'Firma': this.$t('groups.firma'),
        'Administracja': this.$t('groups.administracja'),
        'Flota': this.$t('groups.flota'),
        'Kierowcy': this.$t('groups.kierowcy'),
        'Finanse': this.$t('groups.finanse'),
        'Dokumenty': this.$t('groups.dokumenty'),
        'Import': this.$t('groups.import'),
        'Komunikacja': this.$t('groups.komunikacja'),
        'Marketing': this.$t('groups.marketing'),
        'Wykazy': this.$t('groups.wykazy'),
        'Program Punktowy': this.$t('groups.programPunktowy'),
        'Ustawienia': this.$t('groups.ustawienia'),
      };

      return this.activeGroupDefs
        .map(def => {
          const items = def.pages.filter(p => linkMap[p]).map(p => linkMap[p]);
          return {
            groupName: def.groupName ? (groupTranslations[def.groupName] || def.groupName) : null,
            icon: def.icon,
            items,
          };
        })
        .filter(g => g.items.length > 0);
    },
    standalonePages() {
      const sg = this.menuConfigGroups.find(g => !g.groupName);
      return sg ? sg.pages : [];
    },
    unassignedPages() {
      const assigned = new Set();
      this.menuConfigGroups.forEach(g => {
        g.pages.forEach(p => assigned.add(p));
      });
      return this.links.filter(([, , slug]) => !assigned.has(slug));
    },
    navPages() {
      return this.links.map(([icon, text, link]) => ({ icon, text, link }));
    },
  },
  watch: {
    claimsMobile: {
      immediate: true,
      handler(value) { if (value) this.drawer = false; },
    },
    '$route.path'(val) {
      if (val === '/chat') resetUnreadCount();
      // Świeża wyszukiwarka stron po każdej nawigacji — Vuetify po wyborze
      // dopisuje tekst pozycji swoim timerem, więc reset robimy dwukrotnie
      // z opóźnieniem (natychmiast + po jego wewnętrznym ticku).
      this.selectedPage = null;
      const wipe = () => {
        this.pageSearchText = null;
        try { this.$refs.pageSearch?.setValue?.(null); } catch (e) { /* ignore */ }
      };
      this.$nextTick(wipe);
      setTimeout(wipe, 250);
    },
  },
  async mounted() {
    // Globalna obsługa wygasłej sesji (JWT żyje 24 h): każde 401 poza stroną
    // logowania = czytelny komunikat + przekierowanie do logowania. Wcześniej
    // requesty cicho padały, a użytkownik widział tylko ogólne błędy zapisu
    // (np. "Nie udało się zapisać konfiguracji menu").
    axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const is401 = err?.response?.status === 401;
        const onLogin = (this.$route?.path || '').startsWith('/login');
        if (is401 && !onLogin && localStorage.getItem('token') && !this.__sessionExpiredShown) {
          this.__sessionExpiredShown = true;
          localStorage.removeItem('token');
          Swal.fire({
            icon: 'warning',
            title: 'Sesja wygasła',
            text: 'Twoja sesja wygasła po 24 godzinach. Zaloguj się ponownie.',
            confirmButtonColor: '#3F51B5',
          }).then(() => {
            window.location.href = '/login';
          });
        }
        return Promise.reject(err);
      },
    );
    this.user = localStorage.getItem("user");
    this.loadMenuConfig();
    this.setupInstallPrompt();
    // Patcher dla v-date-picker show-week — wymusza ISO 8601 numerację
    // tygodni (Vuetify 2.6 default ma własną logikę, off-by-one na granicach lat).
    __installIsoWeekPatcher();
    if (this.user === 'true') {
      await this.loadAvailableCompanies();
      await this.fetchActiveCompanyLogo();
      this.fetchMyProfile();
    }
    this.$root.$on('logo-updated', (url) => {
      this.companyLogoUrl = url || this.defaultLogoUrl;
    });
    this.$root.$on('profile-updated', () => {
      this.fetchMyProfile();
    });
    this.$root.$on('invoices-updated', () => {
      this.fetchPendingInvoicesCount();
    });
    this.$root.$on('website-leads-updated', () => {
      this.fetchPendingWebsiteLeadsCount();
    });
    this.$root.$on('notifications-updated', () => {
      this.fetchNotifications();
    });
    this.$root.$on('onboarding-updated', () => {
      this.fetchPendingOnboardingCount();
      this.fetchPendingDocumentsCount();
    });
    // Init global chat socket for notifications on all pages
    if (this.user) initGlobalChat();
    // Cache + dedup zapytań GET (listy referencyjne 60 s, dedup wszystkich GET).
    installHttpCache(axios);
    axios.interceptors.request.use(
      (config) => {
        const currentPath = this.$route?.path || window.location.pathname;
        // Tylko pierwszy człon adresu: z `/wiki/article/<id>` wychodziło
        // `wiki/article/<id>`, czego nikt nie ma w uprawnieniach — pracownik
        // dostawał 403 na każdym artykule Wiki, head admin tego nie widział.
        const currentPage = currentPath.split('/').filter(Boolean)[0] || 'dashboard';
        config.headers['X-Current-Page'] = currentPage;
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    axios.interceptors.response.use(
      (response) => {
        return response;
      },
      (error) => {
        // Lokalizacja błędów na język aplikacji. Podmieniamy tylko znane,
        // angielskie/puste komunikaty (np. "Unauthorized") — własne polskie
        // komunikaty z backendu zostawiamy bez zmian.
        const pl = !this.$i18n || this.$i18n.locale !== "en";
        const status = error.response ? error.response.status : 0;
        const localized = (s) => {
          if (s === 401) return pl ? "Sesja wygasła lub brak autoryzacji. Zaloguj się ponownie." : "Session expired or unauthorized. Please log in again.";
          if (s === 403) return pl ? "Brak uprawnień do tej operacji." : "You do not have permission for this action.";
          if (s === 404) return pl ? "Nie znaleziono zasobu." : "Resource not found.";
          if (s === 429) return pl ? "Za dużo prób. Spróbuj ponownie za chwilę." : "Too many requests. Please try again shortly.";
          if (s === 400) return pl ? "Nieprawidłowe dane żądania." : "Bad request.";
          if (s >= 500) return pl ? "Błąd serwera. Spróbuj ponownie później." : "Server error. Please try again later.";
          if (s === 0) return pl ? "Brak połączenia z serwerem." : "No connection to the server.";
          return pl ? "Wystąpił błąd. Spróbuj ponownie." : "An error occurred. Please try again.";
        };
        const KNOWN_EN = ["Unauthorized", "Forbidden", "Not Found", "Internal Server Error", "Bad Request", "Too Many Requests"];
        if (!error.response) {
          // Brak odpowiedzi (sieć/timeout) — dajemy zlokalizowany komunikat,
          // żeby kod czytający e.response.data.message nie wywalał się.
          error.response = { data: { message: localized(0) }, status: 0 };
        } else {
          const m = error.response.data && error.response.data.message;
          const isEnglishDefault = !m || (typeof m === "string" && KNOWN_EN.includes(m));
          if (isEnglishDefault) {
            if (!error.response.data) error.response.data = {};
            error.response.data.message = localized(status);
          }
        }
        if (status === 401) {
          console.warn("401 Unauthorized — token expired or invalid, redirecting to login");
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          this.user = null;
          this.idleWarnDialog = false;
          stopIdleWatch();
          if (this._notifInterval) {
            clearInterval(this._notifInterval);
            this._notifInterval = null;
          }
          if (this._taskInterval) {
            clearInterval(this._taskInterval);
            this._taskInterval = null;
          }
          if (this.$route.path !== "/login") {
            this.$router.push("/login");
          }
        }
        return Promise.reject(error);
      }
    );
    window.addEventListener("toggleLoading", (event) => {
      this.loading = event.detail.loading == "true" ? true : false;
      this.overlay = event.detail.overlay == "true" ? true : false;
    });

    const allLinks = this.buildLinks();

    const userPages = localStorage.getItem("pages");

    // Menu wg uprawnień: filtrujemy dla KAŻDEJ roli poza head/superadminem.
    // Wcześniej filtr działał tylko dla roli 'user' — np. rola 'office'
    // widziała wszystkie pozycje menu, a backend blokował wejścia (403).
    if (!isPrivilegedAdmin() && userPages) {
      try {
        const allowedPages = JSON.parse(userPages);
        // eslint-disable-next-line no-unused-vars
        this.links = allLinks.filter(([_a, _b, link]) => allowedPages.includes(link));
      } catch (e) {
        console.error("Error parsing user pages:", e);
        this.links = allLinks;
      }
    } else {
      this.links = allLinks;
    }

    // Asystent znika z menu, gdy właściciel wyłączy go w Ustawieniach systemu —
    // inaczej pracownik klikałby w pozycję, która i tak odmawia odpowiedzi.
    this.ukryjAsystentaJesliWylaczony();

    // Initialize notifications only if logged in
    if (localStorage.getItem("token")) {
      this.startIdleGuard();
      // Sygnał życia ewidencji czasu pracy korzysta z TEGO SAMEGO licznika
      // bezczynności — dwa równoległe liczniki dałyby dwa okna ostrzeżenia
      // i dwa wylogowania. Gdy backend odda progi z konfiguracji, przestawiamy
      // na nie licznik, żeby ustawienia w panelu naprawdę działały.
      startWorkTimeHeartbeat({
        onConfig: (config) => {
          this.startIdleGuard({
            idlePauseMin: config.idleMinutes,
            autoLogoutMin: config.autoLogoutMinutes,
            warnBeforeSec: config.warnBeforeLogoutMinutes * 60,
          });
        },
      });
      this.fetchNotifications();
      this.fetchPendingInvoicesCount();
      this.fetchPendingOnboardingCount();
      this.fetchPendingDocumentsCount();
      this.fetchPendingChangesCount();
      this.fetchPendingInspectionsCount();
      this.fetchPendingWebsiteLeadsCount();
      this.lastCheckedTime = new Date();

      // Pop-up o nierozpoczętych zadaniach przypisanych do mnie — od razu po zalogowaniu.
      this.checkMyPendingTasks();

      // Check for new notifications every 10 seconds — ale tylko gdy karta jest
      // aktywna (document.hidden pomija polling w tle, oszczędza zbędne zapytania).
      this._notifInterval = setInterval(() => {
        if (document.hidden) return;
        if (localStorage.getItem("token")) {
          this.checkForNewNotifications();
          this.fetchPendingInvoicesCount();
          this.fetchPendingOnboardingCount();
          this.fetchPendingDocumentsCount();
      this.fetchPendingDocumentsCount();
          this.fetchPendingChangesCount();
          this.fetchPendingInspectionsCount();
          this.fetchPendingWebsiteLeadsCount();
        }
      }, 10000);

      // Pop-up o zadaniach powtarzany co 45 s — aż potwierdzę rozpoczęcie każdego
      // (NEW→IN_PROGRESS). Persistentny modal i tak wymusza akcję, gdy jest otwarty.
      this._taskInterval = setInterval(() => {
        if (document.hidden) return;
        if (localStorage.getItem("token")) this.checkMyPendingTasks();
      }, 45000);

      // Po powrocie do karty od razu jeden odświeżający strzał.
      this._onVisible = () => {
        if (!document.hidden && localStorage.getItem("token")) {
          this.checkForNewNotifications();
          this.fetchPendingInvoicesCount();
          this.fetchPendingOnboardingCount();
          this.fetchPendingDocumentsCount();
      this.fetchPendingDocumentsCount();
          this.fetchPendingChangesCount();
          this.fetchPendingInspectionsCount();
          this.fetchPendingWebsiteLeadsCount();
          this.checkMyPendingTasks();
        }
      };
      document.addEventListener("visibilitychange", this._onVisible);
    }
  },
  beforeDestroy() {
    stopWorkTimeHeartbeat();
  },
  methods: {
    async ukryjAsystentaJesliWylaczony() {
      if (!localStorage.getItem('token')) return;
      try {
        const r = await axios.get(process.env.VUE_APP_ROOT_API + 'assistant/status', {
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const dostepny = !!(r.data && r.data.data && r.data.data.dostepny);
        if (!dostepny) {
          // eslint-disable-next-line no-unused-vars
          this.links = this.links.filter(([_a, _b, link]) => link !== 'assistant');
        }
      } catch (e) {
        // Brak odpowiedzi = nie ryzykujemy martwej pozycji w menu.
        // eslint-disable-next-line no-unused-vars
        this.links = this.links.filter(([_a, _b, link]) => link !== 'assistant');
      }
    },
    // PWA: pasek "Zainstaluj". Chowamy, gdy apka już zainstalowana (standalone)
    // albo użytkownik zamknął pasek. Pasek pokazujemy ZAWSZE (dopóki nie
    // zainstalowana/odrzucona) — nie czekamy na `beforeinstallprompt`, bo iPad/
    // Safari go nie odpala. Gdy prompt jest → instaluje jednym kliknięciem;
    // gdy nie → pokazuje instrukcję dopasowaną do urządzenia.
    setupInstallPrompt() {
      const ua = window.navigator.userAgent || '';
      this.isIOS =
        /iphone|ipad|ipod/i.test(ua) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      const standalone =
        window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true;
      if (standalone || localStorage.getItem('pwaInstallDismissed') === '1') return;
      window.addEventListener('beforeinstallprompt', (e) => {
        e.preventDefault();
        this.installPromptEvent = e;
        this.showInstallPrompt = true;
      });
      window.addEventListener('appinstalled', () => {
        this.showInstallPrompt = false;
        this.installPromptEvent = null;
        localStorage.setItem('pwaInstallDismissed', '1');
      });
      // Pokaż pasek nawet bez auto-promptu (iPad/Safari, albo prompt jeszcze
      // nie wystrzelił) — z opcją instrukcji. Krótkie opóźnienie, by dać szansę
      // natywnemu promptowi Chrome/Androida.
      setTimeout(() => {
        if (!this.showInstallPrompt) this.showInstallPrompt = true;
      }, 3000);
    },
    async installApp() {
      const ev = this.installPromptEvent;
      if (ev) {
        try {
          ev.prompt();
          await ev.userChoice;
          this.installPromptEvent = null;
          this.showInstallPrompt = false;
          return;
        } catch (e) {
          // Prompt zużyty/niedostępny → nie gubimy użytkownika, pokazujemy
          // instrukcję ręcznej instalacji zamiast "nic się nie dzieje".
          this.installPromptEvent = null;
        }
      }
      // Brak (lub nieudany) natywny prompt → instrukcja dopasowana do urządzenia.
      this.installDialog = true;
    },
    dismissInstall() {
      this.showInstallPrompt = false;
      localStorage.setItem('pwaInstallDismissed', '1');
    },
    // Nierozpoczęte zadania przypisane do mnie → pop-up wymuszający potwierdzenie
    // rozpoczęcia. Powtarza się (po logowaniu, co 45 s, po powrocie na kartę),
    // aż wszystkie zostaną ruszone (NEW→IN_PROGRESS).
    async checkMyPendingTasks() {
      if (localStorage.getItem("user") !== "true") return;
      if (this._taskPopupOpen) return;
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "tasks/my-pending",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        const tasks = Array.isArray(res.data) ? res.data : (res.data && res.data.data) || [];
        if (tasks.length) await this.showTaskPopup(tasks);
      } catch (e) {
        /* cichy fail — nie blokujemy panelu */
      }
    },
    async showTaskPopup(tasks) {
      this._taskPopupOpen = true;
      if (!this._dueAcked) this._dueAcked = new Set();
      const esc = (s) =>
        String(s || "").replace(/[&<>"']/g, (c) =>
          ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
        );
      const snooze = this.getTaskSnooze();
      const nowTs = Date.now();
      const pendingTasks = tasks.filter((task) => {
        const dueAlreadyAcknowledged = task.reason === "DUE_SOON" && this._dueAcked.has(task.id);
        const snoozed = snooze[task.id] && snooze[task.id] > nowTs;
        return !dueAlreadyAcknowledged && !snoozed;
      });
      try {
        for (const [taskIndex, t] of pendingTasks.entries()) {
          const isDue = t.reason === "DUE_SOON";
          let deadlineHtml = "";
          if (t.dueDate) {
            const d = new Date(t.dueDate);
            if (!isNaN(d)) {
              deadlineHtml =
                '<div class="btp-task-alert__deadline">' +
                '<span class="btp-task-alert__deadline-icon mdi mdi-calendar-clock" aria-hidden="true"></span>' +
                '<span class="btp-task-alert__deadline-copy">' +
                '<span class="btp-task-alert__deadline-label">' +
                esc(this.$t("tasks.popup.deadline")) +
                "</span>" +
                '<strong class="btp-task-alert__deadline-value">' +
                esc(d.toLocaleString("pl-PL", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })) +
                "</strong>" +
                "</span>" +
                "</div>";
            }
          }
          const alertKind = isDue ? "due" : "new";
          const alertIcon = isDue ? "mdi-clock-alert-outline" : "mdi-clipboard-text-outline";
          const alertKindLabel = isDue
            ? this.$t("tasks.popup.dueLabel")
            : this.$t("tasks.popup.newLabel");
          const counterLabel = this.$t("tasks.popup.counter", {
            current: taskIndex + 1,
            total: pendingTasks.length,
          });
          const r = await Swal.fire({
            title: isDue ? this.$t("tasks.popup.dueTitle") : this.$t("tasks.popup.title"),
            html:
              '<div class="btp-task-alert__content">' +
              '<div class="btp-task-alert__meta">' +
              '<span class="btp-task-alert__kind btp-task-alert__kind--' + alertKind + '">' +
              '<span class="btp-task-alert__kind-icon mdi ' + alertIcon + '" aria-hidden="true"></span>' +
              esc(alertKindLabel) +
              "</span>" +
              '<span class="btp-task-alert__counter">' + esc(counterLabel) + "</span>" +
              "</div>" +
              '<div class="btp-task-alert__task-title">' + esc(t.title) + "</div>" +
              (t.description
                ? '<div class="btp-task-alert__description">' + esc(t.description) + "</div>"
                : "") +
              deadlineHtml +
              "</div>",
            confirmButtonText: isDue ? this.$t("tasks.popup.ack") : this.$t("tasks.popup.start"),
            showDenyButton: true,
            denyButtonText: this.$t("tasks.popup.snooze"),
            allowOutsideClick: false,
            allowEscapeKey: false,
            showCancelButton: true,
            cancelButtonText: this.$t("tasks.popup.postpone"),
            background: "#121c2a",
            color: "#ffffff",
            customClass: {
              popup: "btp-task-alert",
              title: "btp-task-alert-title",
              htmlContainer: "btp-task-alert-body",
              actions: "btp-task-alert-actions",
              confirmButton: "btp-task-alert-btn",
              denyButton: "btp-task-alert-snooze",
              cancelButton: "btp-task-alert-snooze",
            },
          });
          if (r.isConfirmed) {
            if (isDue) {
              // Termin — tylko potwierdzenie „widzę", bez zmiany statusu.
              this._dueAcked.add(t.id);
            } else {
              try {
                await axios({
                  method: "put",
                  url: process.env.VUE_APP_ROOT_API + "tasks/" + t.id + "/start",
                  headers: { Authorization: "Bearer " + localStorage.getItem("token") },
                });
              } catch (e) {
                /* jak start się nie uda, zadanie zostaje NEW i pop-up wróci */
              }
            }
          } else if (r.isDenied) {
            // „Przypomnij za 1 godzinę" — chowamy pop-up na godzinę; po jej upływie
            // (jeśli zadanie wciąż niezrobione) wyskoczy ponownie.
            this.snoozeTask(t.id);
          } else if (r.dismiss === Swal.DismissReason.cancel) {
            // „Odrocz 1 dzień" — pop-up wróci jutro (zadanie bez zmian).
            this.snoozeTask(t.id, 24 * 60 * 60 * 1000);
          }
        }
      } finally {
        this._taskPopupOpen = false;
      }
    },
    getTaskSnooze() {
      try {
        return JSON.parse(localStorage.getItem("taskSnooze") || "{}") || {};
      } catch (e) {
        return {};
      }
    },
    snoozeTask(id, ms = 60 * 60 * 1000) {
      const s = this.getTaskSnooze();
      const now = Date.now();
      s[id] = now + ms; // domyślnie 1 godzina
      // wyczyść wygasłe wpisy, żeby localStorage nie puchł
      Object.keys(s).forEach((k) => {
        if (!s[k] || s[k] <= now) delete s[k];
      });
      try {
        localStorage.setItem("taskSnooze", JSON.stringify(s));
      } catch (e) {
        /* brak miejsca / prywatny tryb — trudno, pop-up wróci przy następnym pollu */
      }
    },
    async fetchMyProfile() {
      try {
        const me = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "company-admins/me",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        const d = me.data.data || {};
        this.myProfile = {
          firstName: d.firstName || '',
          lastName: d.lastName || '',
          email: d.email || '',
          role: d.role || localStorage.getItem("role") || '',
          avatar: d.avatar || null,
        };
      } catch (e) {
        // brak profilu nie blokuje aplikacji
        this.myProfile.role = localStorage.getItem("role") || '';
      }
    },
    roleLabel(role) {
      const map = {
        headadmin: 'Head admin',
        superadmin: 'Administrator',
        user: 'Manager',
        office: 'Biuro',
        admin: 'Administrator',
      };
      return map[role] || role || '';
    },
    goToAccountSettings() {
      if (this.$route.path !== '/account-settings') {
        this.$router.push('/account-settings').catch(() => {});
      }
    },
    toggleLocale() {
      const newLocale = this.$i18n.locale === 'pl' ? 'en' : 'pl';
      this.$i18n.locale = newLocale;
      this.$vuetify.lang.current = newLocale;
      localStorage.setItem('adminLocale', newLocale);
      // Zsynchronizuj <html lang>, by przeglądarka nie auto-tłumaczyła treści
      // (inaczej marki jak „Bolt" stają się „śruba").
      document.documentElement.lang = newLocale;
      this.links = this.buildLinks();
    },
    buildLinks() {
      const slugToKey = {
        'dashboard': 'dashboard', 'company': 'company', 'subcompanies': 'subcompanies',
        'marketing-posts': 'marketingPosts',
        'bank-accounts': 'bankAccounts', 'admins': 'admins', 'fleet': 'fleet',
        'driver-fleet': 'driverFleet', 'rent-to-own': 'rentToOwn', 'crashes': 'crashes', 'claims': 'claims', 'fuel-cards': 'fuelCards', 'inspections': 'inspections',
        'drivers': 'drivers', 'driver-map': 'driverMap', 'drivers-archive': 'driversArchive', 'bonuses': 'bonuses',
        'accounting': 'accounting', 'invoices': 'invoices', 'cost-invoices': 'costInvoices', 'incomes': 'incomes',
        'document-templates': 'documentTemplates', 'pending-documents': 'pendingDocuments',
        'driver-onboarding': 'driverOnboarding', 'pending-drivers-changes': 'pendingDriversChanges',
        'uploader': 'uploader', 'uploader-templates': 'uploaderTemplates',
        'import-history': 'importHistory', 'settlements': 'settlements',
        'contracts': 'contracts', 'payouts': 'payouts', 'debtors': 'debtors', 'daily-payouts': 'dailyPayouts', 'self-service-payouts': 'selfServicePayouts', 'bolt-sync-drivers': 'boltSyncDrivers', 'receipts': 'receipts',
        'cash-report': 'cashReport', 'komornik-letters': 'komornikLetters', 'assistant': 'assistant',
        'policy-sales': 'policySales',
        'tasks': 'tasks', 'notifications': 'notifications',
        'notification-logs': 'notificationLogs', 'payout-config': 'payoutConfig',
        'wiki': 'wiki', 'audit-logs': 'auditLogs', 'system-settings': 'systemSettings', 'mail-config': 'mailConfig', 'app-version': 'appVersion',
        'chat': 'chat', 'documents-repository': 'documentsRepository', 'official-documents': 'officialDocuments', 'company-licenses': 'companyLicenses', 'legalization': 'legalization',
        'announcements': 'announcements', 'website-leads': 'websiteLeads', 'sms': 'sms', 'email': 'email', 'hr': 'hr',
        'work-time': 'workTime',
        'wykaz-pojazdy': 'wykazPojazdy', 'wykaz-kierowcy': 'wykazKierowcy',
        'staff-activity': 'staffActivity',
        'points-settings': 'pointsSettings', 'points-referrals': 'pointsReferrals',
        'points-reviews': 'pointsReviews', 'points-reward-orders': 'pointsRewardOrders',
        'points-drivers': 'pointsDrivers',
        'integrations': 'integrations',
      };
      const slugToIcon = {
        'dashboard': 'mdi-monitor-dashboard', 'company': 'mdi-domain', 'subcompanies': 'mdi-domain-plus',
        'marketing-posts': 'mdi-bullhorn-variant',
        'bank-accounts': 'mdi-bank', 'admins': 'mdi-account', 'fleet': 'mdi-car-multiple',
        'driver-fleet': 'mdi-car-key', 'rent-to-own': 'mdi-car-clock', 'crashes': 'mdi-tow-truck', 'claims': 'mdi-clipboard-text-search-outline', 'fuel-cards': 'mdi-gas-station', 'inspections': 'mdi-clipboard-check-outline',
        'drivers': 'mdi-taxi', 'driver-map': 'mdi-map-marker-multiple', 'drivers-archive': 'mdi-archive', 'bonuses': 'mdi-plus-box-multiple',
        'invoices': 'mdi-invoice-text-edit', 'cost-invoices': 'mdi-invoice-text-check',
        'incomes': 'mdi-cash-fast', 'document-templates': 'mdi-file-document-edit',
        'pending-documents': 'mdi-file-clock', 'driver-onboarding': 'mdi-account-clock',
        'pending-drivers-changes': 'mdi-account-edit', 'uploader': 'mdi-import',
        'uploader-templates': 'mdi-function-variant', 'import-history': 'mdi-history',
        'settlements': 'mdi-cash-sync', 'contracts': 'mdi-cash-refund',
        'payouts': 'mdi-cash-check', 'debtors': 'mdi-account-alert', 'daily-payouts': 'mdi-cash-clock', 'self-service-payouts': 'mdi-cash-fast', 'bolt-sync-drivers': 'mdi-sync', 'receipts': 'mdi-receipt-text',
        'cash-report': 'mdi-cash-register', 'komornik-letters': 'mdi-gavel', 'assistant': 'mdi-robot-happy-outline',
        'policy-sales': 'mdi-file-certificate',
        'tasks': 'mdi-checkbox-marked-outline', 'notifications': 'mdi-bell',
        'notification-logs': 'mdi-history', 'payout-config': 'mdi-cog',
        'wiki': 'mdi-book-open-variant', 'audit-logs': 'mdi-history',
        'system-settings': 'mdi-cog-outline', 'mail-config': 'mdi-email-edit', 'app-version': 'mdi-cellphone-arrow-down', 'chat': 'mdi-chat',
        'documents-repository': 'mdi-folder-multiple', 'official-documents': 'mdi-bank-outline', 'company-licenses': 'mdi-certificate-outline', 'legalization': 'mdi-passport', 'announcements': 'mdi-bullhorn', 'website-leads': 'mdi-web', 'hr': 'mdi-account-heart',
        'work-time': 'mdi-clock-outline',
        'accounting': 'mdi-calculator-variant', 'sms': 'mdi-message-text', 'email': 'mdi-email', 'wykaz-pojazdy': 'mdi-clipboard-list',
        'wykaz-kierowcy': 'mdi-card-account-details',
        'staff-activity': 'mdi-account-clock',
        'points-settings': 'mdi-star-cog', 'points-referrals': 'mdi-account-group',
        'points-reviews': 'mdi-star-check', 'points-reward-orders': 'mdi-gift',
        'points-drivers': 'mdi-star-face',
        'integrations': 'mdi-api',
      };
      const linki = Object.keys(slugToKey).map(slug => [
        slugToIcon[slug] || 'mdi-circle',
        this.$t(`nav.${slugToKey[slug]}`),
        slug,
      ]);
      if (this.isHeadAdmin && this.linkiZewnetrzne['usage-stats']) {
        linki.push(['mdi-chart-box-outline', this.$t('nav.usageStats'), 'usage-stats']);
      }
      return linki;
    },
    // Pozycja menu: albo trasa panelu, albo adres zewnetrzny w nowej karcie.
    atrybutyPozycji(link) {
      const url = this.linkiZewnetrzne[link];
      return url ? { href: url, target: '_blank', rel: 'noopener' } : { to: '/' + link };
    },
    navigateToPage(link) {
      if (link && this.linkiZewnetrzne[link]) {
        window.open(this.linkiZewnetrzne[link], '_blank', 'noopener');
        this.selectedPage = null;
        this.$nextTick(() => {
          setTimeout(() => {
            this.pageSearchText = null;
            this.$refs.pageSearch?.blur?.();
          }, 60);
        });
        return;
      }
      if (link) {
        const target = '/' + link;
        if (this.$route.path !== target) {
          this.$router.push(target).catch((err) => {
            if (err && err.name !== 'NavigationDuplicated') throw err;
          });
        }
        this.selectedPage = null;
        // Wyczyść też wpisany tekst — bez tego pole trzyma stare hasło,
        // a ponowne wpisanie tego samego nie wyzwala wyszukiwania.
        this.$nextTick(() => {
          setTimeout(() => {
            this.pageSearchText = null;
            this.$refs.pageSearch?.blur?.();
          }, 60);
        });
      }
    },
    isGroupActive(group) {
      const currentPath = this.$route?.path?.replace('/', '') || '';
      return group.items.some(([, , link]) => link === currentPath);
    },
    // Menu config methods
    openMenuConfig() {
      this.menuConfigGroups = JSON.parse(JSON.stringify(this.activeGroupDefs));
      this.editingGroupIdx = -1;
      this.newGroupName = '';
      this.newGroupIcon = 'mdi-folder';
      this.menuConfigDialog = true;
    },
    addMenuGroup() {
      if (!this.newGroupName) return;
      this.menuConfigGroups.push({
        groupName: this.newGroupName,
        icon: this.newGroupIcon,
        pages: [],
      });
      this.newGroupName = '';
      this.newGroupIcon = 'mdi-folder';
    },
    removeMenuGroup(idx) {
      this.menuConfigGroups.splice(idx, 1);
    },
    startEditGroup(idx) {
      this.editingGroupIdx = idx;
      this.editingGroupName = this.menuConfigGroups[idx].groupName;
      this.editingGroupIcon = this.menuConfigGroups[idx].icon;
    },
    saveEditGroup(idx) {
      this.menuConfigGroups[idx].groupName = this.editingGroupName;
      this.menuConfigGroups[idx].icon = this.editingGroupIcon;
      this.editingGroupIdx = -1;
    },
    onDragStart(event, type, payload, sourceGroup) {
      this.dragType = type;
      this.dragPayload = payload;
      this.dragSourceGroup = sourceGroup !== undefined ? sourceGroup : null;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', JSON.stringify({ type, payload }));
    },
    onDragEnd() {
      this.dragType = null;
      this.dragPayload = null;
      this.dragSourceGroup = null;
      this.dragOverGroup = null;
      this.dragOverPage = null;
    },
    onGroupDragEnter(gIdx) {
      this.dragOverGroup = gIdx;
    },
    onPageDragOver(event, gIdx, pIdx) {
      if (this.dragType !== 'page') return;
      this.dragOverPage = { group: gIdx, index: pIdx };
    },
    onDropToGroup(event, targetIdx) {
      event.preventDefault();
      const raw = event.dataTransfer.getData('text/plain');
      if (!raw) return;
      try {
        const { type, payload } = JSON.parse(raw);
        if (type === 'group') {
          // Reorder groups
          const fromIdx = payload;
          if (fromIdx === targetIdx) return;
          const moved = this.menuConfigGroups.splice(fromIdx, 1)[0];
          this.menuConfigGroups.splice(targetIdx, 0, moved);
        } else if (type === 'page') {
          // Move page to end of target group
          this.movePageToGroup(payload, targetIdx, -1);
        }
      } catch (e) { /* ignore */ }
      this.onDragEnd();
    },
    onDropPageAt(event, gIdx, pIdx) {
      event.preventDefault();
      const raw = event.dataTransfer.getData('text/plain');
      if (!raw) return;
      try {
        const { type, payload } = JSON.parse(raw);
        if (type === 'page') {
          this.movePageToGroup(payload, gIdx, pIdx);
        }
      } catch (e) { /* ignore */ }
      this.onDragEnd();
    },
    onDropToUnassigned(event) {
      event.preventDefault();
      const raw = event.dataTransfer.getData('text/plain');
      if (!raw) return;
      try {
        const { type, payload } = JSON.parse(raw);
        if (type === 'page') {
          this.menuConfigGroups.forEach(g => {
            const idx = g.pages.indexOf(payload);
            if (idx !== -1) g.pages.splice(idx, 1);
          });
          // Clean up empty standalone groups
          this.menuConfigGroups = this.menuConfigGroups.filter(g => g.groupName || g.pages.length > 0);
        }
      } catch (e) { /* ignore */ }
      this.onDragEnd();
    },
    onDropToStandalone(event) {
      event.preventDefault();
      const raw = event.dataTransfer.getData('text/plain');
      if (!raw) return;
      try {
        const { type, payload } = JSON.parse(raw);
        if (type === 'page') {
          // Remove from named groups
          this.menuConfigGroups.forEach(g => {
            if (g.groupName) {
              const idx = g.pages.indexOf(payload);
              if (idx !== -1) g.pages.splice(idx, 1);
            }
          });
          // Add to standalone group
          let sg = this.menuConfigGroups.find(g => !g.groupName);
          if (!sg) {
            sg = { groupName: null, icon: null, pages: [] };
            this.menuConfigGroups.unshift(sg);
          }
          if (!sg.pages.includes(payload)) {
            sg.pages.push(payload);
          }
        }
      } catch (e) { /* ignore */ }
      this.onDragEnd();
    },
    removeStandalonePage(pageSlug) {
      const sg = this.menuConfigGroups.find(g => !g.groupName);
      if (sg) {
        const idx = sg.pages.indexOf(pageSlug);
        if (idx !== -1) sg.pages.splice(idx, 1);
      }
    },
    movePageToGroup(pageSlug, targetGroupIdx, insertAt) {
      // Track if removing from same group before insert position
      let removedBeforeInsert = false;
      this.menuConfigGroups.forEach((g, gi) => {
        const idx = g.pages.indexOf(pageSlug);
        if (idx !== -1) {
          if (gi === targetGroupIdx && idx < insertAt) {
            removedBeforeInsert = true;
          }
          g.pages.splice(idx, 1);
        }
      });
      // Adjust insert position if we removed from before it in same group
      let finalIdx = insertAt;
      if (removedBeforeInsert && insertAt > 0) {
        finalIdx = insertAt - 1;
      }
      if (finalIdx < 0 || finalIdx >= this.menuConfigGroups[targetGroupIdx].pages.length) {
        this.menuConfigGroups[targetGroupIdx].pages.push(pageSlug);
      } else {
        this.menuConfigGroups[targetGroupIdx].pages.splice(finalIdx, 0, pageSlug);
      }
    },
    removePageFromGroup(groupIdx, pageSlug) {
      const idx = this.menuConfigGroups[groupIdx].pages.indexOf(pageSlug);
      if (idx !== -1) this.menuConfigGroups[groupIdx].pages.splice(idx, 1);
    },
    getPageIcon(slug) {
      const found = this.links.find(([, , link]) => link === slug);
      return found ? found[0] : 'mdi-help';
    },
    getPageName(slug) {
      const found = this.links.find(([, , link]) => link === slug);
      return found ? found[1] : slug;
    },
    async saveMenuConfig() {
      const config = this.menuConfigGroups.filter(g => g.groupName || g.pages.length > 0);
      try {
        await axios({
          method: "put",
          url: process.env.VUE_APP_ROOT_API + "menu-config",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
          data: { groups: config },
        });
        this.customGroupDefs = config;
        this.menuConfigDialog = false;
      } catch (e) {
        console.error('Failed to save menu config:', e);
        if (e?.response?.status === 401) return; // obsłużone globalnie (sesja wygasła)
        const detail = e?.response?.data?.message
          ? (Array.isArray(e.response.data.message) ? e.response.data.message.join('; ') : e.response.data.message)
          : e.message;
        Swal.fire({
          icon: 'error',
          title: 'Błąd',
          text: `Nie udało się zapisać konfiguracji menu: ${detail}`,
          confirmButtonColor: '#3F51B5',
        });
      }
    },
    async resetMenuConfig() {
      try {
        await axios({
          method: "delete",
          url: process.env.VUE_APP_ROOT_API + "menu-config",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });
        this.customGroupDefs = null;
        this.menuConfigGroups = JSON.parse(JSON.stringify(this.defaultGroupDefs));
      } catch (e) {
        console.error('Failed to reset menu config:', e);
      }
    },
    async loadMenuConfig() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "menu-config",
          headers: {
            Authorization: "Bearer " + token,
          },
        });
        if (response.data.data) {
          this.customGroupDefs = response.data.data;
        }
      } catch (e) {
        console.error('Failed to load menu config:', e);
      }
    },
    async fetchPendingInvoicesCount() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'invoices/pending-count',
          headers: { Authorization: 'Bearer ' + token },
        });
        this.pendingInvoicesCount = res.data?.data?.count ?? 0;
      } catch (e) {
        console.error('Failed to fetch pending invoices count:', e);
      }
    },
    async fetchPendingOnboardingCount() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'driver-registration/admin/pending-count',
          headers: { Authorization: 'Bearer ' + token },
        });
        this.pendingOnboardingCount = res.data?.data?.count ?? 0;
      } catch (e) {
        console.error('Failed to fetch pending onboarding count:', e);
      }
    },
    // Ile dokumentów czeka na akceptację — bez wchodzenia w zakładkę.
    async fetchPendingDocumentsCount() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'documents/pending-documents/count',
          headers: { Authorization: 'Bearer ' + token },
        });
        this.pendingDocumentsCount = res.data?.data?.count ?? 0;
      } catch (e) {
        console.error('Failed to fetch pending documents count:', e);
      }
    },
    async fetchPendingChangesCount() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'drivers/pending-changes-count',
          headers: { Authorization: 'Bearer ' + token },
        });
        this.pendingChangesCount = res.data?.data?.count ?? 0;
      } catch (e) {
        console.error('Failed to fetch pending changes count:', e);
      }
    },
    // Licznik menu „Zgłoszenia ze strony": leady ze statusem NEW.
    async fetchPendingWebsiteLeadsCount() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'website-leads/pending-count',
          headers: { Authorization: 'Bearer ' + token },
        });
        this.pendingWebsiteLeadsCount = res.data?.data?.count ?? 0;
      } catch (e) {
        console.error('Failed to fetch pending website leads count:', e);
      }
    },
    // Licznik menu „Inspekcje": inspekcje do przeglądu + przeterminowane wezwania.
    async fetchPendingInspectionsCount() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const [rev, sum] = await Promise.all([
          axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'car-inspections/pending-review', headers: { Authorization: 'Bearer ' + token } }).catch(() => null),
          axios({ method: 'get', url: process.env.VUE_APP_ROOT_API + 'car-inspections/active-summons', headers: { Authorization: 'Bearer ' + token } }).catch(() => null),
        ]);
        const reviews = (rev && rev.data && rev.data.data) || [];
        const summons = (sum && sum.data && sum.data.data) || [];
        const overdue = summons.filter((s) => s.overdue).length;
        this.pendingInspectionsCount = reviews.length + overdue;
      } catch (e) {
        console.error('Failed to fetch pending inspections count:', e);
      }
    },
    async fetchActiveCompanyLogo() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const activeId = localStorage.getItem("activeCompanyId");
        if (!activeId) {
          this.companyLogoUrl = this.defaultLogoUrl;
          return;
        }
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'company/' + activeId,
          headers: { Authorization: 'Bearer ' + token },
        });
        const company = res.data && res.data.data;
        this.companyLogoUrl = (company && company.logoUrl) ? company.logoUrl : this.defaultLogoUrl;
      } catch (e) {
        console.error('Failed to fetch company logo:', e);
        this.companyLogoUrl = this.defaultLogoUrl;
      }
    },
    async loadAvailableCompanies() {
      try {
        const token = localStorage.getItem("token");
        if (!token) return;
        const res = await axios({
          method: 'get',
          url: process.env.VUE_APP_ROOT_API + 'company',
          headers: { Authorization: 'Bearer ' + token },
        });
        const list = (res.data && res.data.data) ? res.data.data : [];
        this.availableCompanies = list.map((c) => ({ id: c.id, name: c.name }));
        // Never override activeCompanyId based on list order — that pinned the
        // topbar to a company different from the JWT's bound companyId, so the
        // user saw "ProInvest" while the API returned BTP data. The active
        // company is set from the login response (or by an explicit switch);
        // here we just refresh the displayed name if the stored id is in list.
        if (this.activeCompanyId) {
          const match = list.find((c) => c.id === this.activeCompanyId);
          if (match) {
            this.activeCompanyName = match.name;
            localStorage.setItem("activeCompanyName", match.name);
          }
        }
      } catch (e) {
        console.error('Failed to load companies:', e);
      }
    },
    async stopImpersonation() {
      this.stoppingImp = true;
      try {
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'admin-auth/stop-impersonation',
          headers: { Authorization: 'Bearer ' + localStorage.getItem('token') },
        });
        const d = res.data;
        localStorage.setItem('token', d.access_token);
        localStorage.setItem('role', d.role);
        localStorage.setItem('actualRole', d.actualRole || d.role || '');
        localStorage.setItem('isHead', String(!!d.isHead));
        localStorage.setItem('pages', JSON.stringify(d.pages || []));
        localStorage.setItem('pagesWithEdit', JSON.stringify(d.pagesWithEdit || []));
        if (d.companyId) localStorage.setItem('activeCompanyId', d.companyId);
        if (d.companyName) localStorage.setItem('activeCompanyName', d.companyName);
        window.location.href = '/';
      } catch (e) {
        this.stoppingImp = false;
        const msg = e?.response?.data?.message?.toString?.() || 'Nie udało się wrócić do swojego konta';
        this.snackbarColor = '#D32F2F';
        this.snackbarIcon = 'mdi-alert-circle';
        this.snackbarText = msg;
        this.snackbar = true;
      }
    },
    async switchCompany(company) {
      if (!company || company.id === this.activeCompanyId) return;
      this.switchingCompany = true;
      try {
        const token = localStorage.getItem("token");
        const res = await axios({
          method: 'post',
          url: process.env.VUE_APP_ROOT_API + 'admin-auth/switch-company/' + company.id,
          headers: { Authorization: 'Bearer ' + token },
        });
        if (res.data && res.data.access_token) {
          localStorage.setItem("token", res.data.access_token);
          localStorage.setItem("activeCompanyId", company.id);
          localStorage.setItem("activeCompanyName", company.name);
          this.snackbarColor = '#3F51B5';
          this.snackbarIcon = 'mdi-office-building-marker';
          this.snackbarText = 'Pracujesz teraz na: ' + company.name;
          this.snackbarTimeout = 1500;
          this.snackbar = true;
          setTimeout(() => window.location.reload(), 1200);
        }
      } catch (e) {
        const msg = e?.response?.data?.message?.toString?.() || e?.message || 'Nie udało się zmienić firmy';
        this.snackbarColor = '#D32F2F';
        this.snackbarIcon = 'mdi-alert-circle';
        this.snackbarText = msg;
        this.snackbarTimeout = 3500;
        this.snackbar = true;
      }
      this.switchingCompany = false;
    },
    goBack() {
      this.$router.go(-1);
    },
    /**
     * Auto-wylogowanie po bezczynności. Minutę przed końcem pokazujemy pytanie;
     * w tej fazie sesję przedłuża wyłącznie kliknięcie, nie sam ruch myszy.
     * Progi biorą się z konfiguracji ewidencji czasu pracy, o ile ją włączono.
     */
    startIdleGuard(thresholds) {
      startIdleWatch({
        onWarn: (secondsLeft) => {
          this.idleSecondsLeft = secondsLeft;
          this.idleWarnDialog = true;
        },
        onWarnTick: (secondsLeft) => { this.idleSecondsLeft = secondsLeft; },
        onActive: () => { this.idleWarnDialog = false; },
        onTimeout: () => {
          this.idleWarnDialog = false;
          // TIMEOUT, nie LOGOUT: sesja pracy kończy się na ostatniej aktywności,
          // bo te 45 minut bezczynności nie jest czasem pracy.
          this.logout('TIMEOUT');
        },
      }, thresholds);
    },
    stayLoggedIn() {
      this.idleWarnDialog = false;
      markActivity(true);
    },
    async logout(reason = 'LOGOUT') {
      stopIdleWatch();
      stopWorkTimeHeartbeat();
      // Sesję pracy zamykamy PRZED usunięciem tokenu — inaczej backend nie ma
      // czym uwierzytelnić żądania i dzień zostaje z otwartą sesją do crona.
      await closeWorkSession(reason);
      clearHttpCache();
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
    },
    async fetchNotifications() {
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "notifications/user/all?take=10&skip=1",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });

        this.notifications = response.data.data.result.map(n => ({
          id: n.id,
          title: this.getNotificationTitle(n),
          message: n.text,
          timestamp: new Date(n.createdAt),
          read: n.read,
          type: n.json?.type || "general",
          json: n.json
        }));

        this.updateUnreadCount();
      } catch (e) {
        console.error('Failed to fetch notifications:', e);
      }
    },
    async checkForNewNotifications() {
      try {
        const response = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "notifications/user/unread?take=10&skip=1",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        });

        const newUnreadCount = response.data.data.count;

        if (newUnreadCount !== this.unreadNotificationsCount) {
          this.unreadNotificationsCount = newUnreadCount;
          // Odśwież pełną listę żeby nowa notyfikacja była gotowa do pokazania po kliknięciu dzwonka
          this.fetchNotifications();
        }
      } catch (e) {
        console.error('Failed to check for new notifications:', e);
      }
    },
    async updateUnreadCount() {
      // Jedno źródło prawdy: liczba nieprzeczytanych z backendu.
      // Wcześniej liczyliśmy z this.notifications.filter() — ale ta lista
      // ma max 10 elementów (take=10), więc badge "skakał" między ~9
      // (zaniżone z próbki) a prawdziwym totalem z pollingu (np. 558).
      try {
        const res = await axios({
          method: "get",
          url: process.env.VUE_APP_ROOT_API + "notifications/user/unread?take=1&skip=1",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.unreadNotificationsCount = res.data.data.count || 0;
      } catch (e) {
        // przy błędzie nie zmieniaj licznika (nie zeruj fałszywie)
      }
    },
    showNewNotificationAlert(notification) {
      Swal.fire({
        title: 'Nowe powiadomienie!',
        text: notification.message,
        icon: 'info',
        showConfirmButton: true,
        showCancelButton: true,
        confirmButtonText: 'Zobacz wszystkie',
        cancelButtonText: 'OK',
        confirmButtonColor: '#3F51B5',
        timer: 5000,
        timerProgressBar: true
      }).then((result) => {
        if (result.isConfirmed) {
          this.$router.push('/notifications');
        }
      });
    },
    openNotificationsPreview() {
      this.notificationsMenu = true;
      this.fetchNotifications();
    },
    async markAllNotificationsRead() {
      this.markingAllRead = true;
      try {
        await axios({
          method: "patch",
          url: process.env.VUE_APP_ROOT_API + "notifications/user/read-all",
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        });
        this.notifications.forEach((n) => (n.read = true));
        await this.updateUnreadCount();
        this.$root.$emit('task-notifications-updated');
      } catch (e) {
        console.error("Failed to mark all notifications as read:", e);
      }
      this.markingAllRead = false;
    },
    showAllNotifications() {
      this.notificationsMenu = false;
      this.$router.push('/notifications');
    },
    showNotifications() {
      this.notifications.forEach(n => n.read = true);
      this.updateUnreadCount();
      this.$router.push('/notifications');
    },
    getLastNotifications(count) {
      return this.notifications.slice(0, count);
    },
    getNotificationIcon(type) {
      const icons = {
        'ADMIN_NEW_DRIVER_REGISTRATION': 'mdi-account-plus',
        'ADMIN_DOCUMENTS_SUBMITTED': 'mdi-file-document-check',
        'ADMIN_NEW_TASK': 'mdi-clipboard-alert',
        'ADMIN_TASK_NOTE_FROM_DRIVER': 'mdi-comment-text',
        'ADMIN_DRIVER_CHANGES_REQUEST': 'mdi-account-edit',
        'ADMIN_INVOICE_SUBMITTED': 'mdi-file-document-check',
        'ADMIN_IMPORT_ERROR': 'mdi-alert-circle',
        'ADMIN_VEHICLE_INSURANCE_EXPIRING': 'mdi-shield-alert',
        'ADMIN_VEHICLE_INSPECTION_EXPIRING': 'mdi-car-wrench',
        'ADMIN_VEHICLE_SERVICE_DUE': 'mdi-car-cog',
        'ADMIN_DRIVER_SERVICE_SUBMITTED': 'mdi-car-wrench',
        'ADMIN_CRASH_REPORTED': 'mdi-car-emergency',
        'DRIVER_CHANGES_APPROVED': 'mdi-check-circle',
        'DRIVER_CHANGES_REJECTED': 'mdi-close-circle',
        'TASK_ASSIGNED': 'mdi-clipboard-account',
        'TASK_STATUS_CHANGED': 'mdi-clipboard-text',
        'TASK_NOTE_ADDED': 'mdi-comment-text',
        'DOCUMENT_EXPIRING_30': 'mdi-file-clock',
        'DOCUMENT_EXPIRING_7': 'mdi-file-alert',
        'DOCUMENT_EXPIRED': 'mdi-file-cancel',
      };
      return icons[type] || 'mdi-bell';
    },
    getNotificationColor(type) {
      const colors = {
        'ADMIN_NEW_DRIVER_REGISTRATION': '#42A5F5',
        'ADMIN_DOCUMENTS_SUBMITTED': '#00BCD4',
        'ADMIN_NEW_TASK': '#FF9800',
        'ADMIN_TASK_NOTE_FROM_DRIVER': '#42A5F5',
        'ADMIN_DRIVER_CHANGES_REQUEST': '#9C27B0',
        'ADMIN_INVOICE_SUBMITTED': '#4CAF50',
        'ADMIN_IMPORT_ERROR': '#F44336',
        'ADMIN_VEHICLE_INSURANCE_EXPIRING': '#FF9800',
        'ADMIN_VEHICLE_INSPECTION_EXPIRING': '#FF9800',
        'ADMIN_VEHICLE_SERVICE_DUE': '#FF9800',
        'ADMIN_DRIVER_SERVICE_SUBMITTED': '#FB8C00',
        'ADMIN_CRASH_REPORTED': '#F44336',
        'TASK_ASSIGNED': '#4CAF50',
        'DOCUMENT_EXPIRING_7': '#FF9800',
        'DOCUMENT_EXPIRED': '#F44336',
      };
      return colors[type] || '#90A4AE';
    },
    formatTime(timestamp) {
      const now = new Date();
      const diff = now - new Date(timestamp);
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);

      if (minutes < 1) return 'Teraz';
      if (minutes < 60) return `${minutes} min temu`;
      if (hours < 24) return `${hours}h temu`;
      if (days < 7) return `${days} dni temu`;

      return new Date(timestamp).toLocaleDateString('pl-PL');
    },
    async markAsReadAndNavigate(notification) {
      if (!notification.read) {
        try {
          await axios({
            method: "patch",
            url: process.env.VUE_APP_ROOT_API + `notifications/user/read/${notification.id}`,
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
            },
          });
          notification.read = true;
        } catch (e) {
          console.error('Failed to mark notification as read:', e);
        }
      }

      this.updateUnreadCount();
      if (notification.type === 'ADMIN_TASK_NOTE_FROM_DRIVER') {
        this.$root.$emit('task-notifications-updated');
      }
      this.notificationsMenu = false;

      const routes = {
        'ADMIN_NEW_DRIVER_REGISTRATION': '/driver-onboarding',
        'ADMIN_DOCUMENTS_SUBMITTED': '/pending-documents',
        'ADMIN_NEW_TASK': '/tasks',
        'ADMIN_TASK_NOTE_FROM_DRIVER': '/tasks',
        'ADMIN_DRIVER_CHANGES_REQUEST': '/pending-drivers-changes',
        'ADMIN_INVOICE_SUBMITTED': '/invoices',
        'ADMIN_B2B_INVOICE_SUBMITTED': '/settlements',
        'ADMIN_WEBSITE_LEAD': '/website-leads',
        'ADMIN_OWNER_REGISTRATION': '/driver-onboarding',
        'ADMIN_OWNER_PROTOCOL': '/sub-fleet',
        'ADMIN_DRIVER_ZUS_CHANGE': '/drivers',
        'ADMIN_IMPORT_ERROR': '/import-history',
        'ADMIN_VEHICLE_INSURANCE_EXPIRING': '/fleet',
        'ADMIN_VEHICLE_INSPECTION_EXPIRING': '/fleet',
        'ADMIN_VEHICLE_SERVICE_DUE': '/fleet',
        'ADMIN_DRIVER_SERVICE_SUBMITTED': '/fleet',
        'ADMIN_CRASH_REPORTED': '/crashes',
        'DRIVER_CHANGES_APPROVED': '/pending-drivers-changes',
        'DRIVER_CHANGES_REJECTED': '/pending-drivers-changes',
        'TASK_ASSIGNED': '/tasks',
        'TASK_STATUS_CHANGED': '/tasks',
        'DOCUMENT_EXPIRING_30': '/pending-documents',
        'DOCUMENT_EXPIRING_7': '/pending-documents',
        'DOCUMENT_EXPIRED': '/pending-documents',
      };

      // Jeśli powiadomienie ma `driverId` w json, idź bezpośrednio do profilu kierowcy
      const driverId = notification.json?.driverId;
      let route = routes[notification.type] || '/notifications';
      if (driverId && (notification.type === 'DOCUMENT_EXPIRED' || notification.type === 'DOCUMENT_EXPIRING_7' || notification.type === 'DOCUMENT_EXPIRING_30')) {
        route = `/drivers/${driverId}`;
      }
      // Serwis od kierowcy → historia konkretnego auta z podświetlonym wpisem.
      if (notification.type === 'ADMIN_DRIVER_SERVICE_SUBMITTED' && notification.json?.carId) {
        const hid = notification.json.historyId ? `&historyId=${notification.json.historyId}` : '';
        // FleetDetails czyta parametry w created() — przy przejściu z innej
        // strony router wystarczy; w obrębie tej samej strony wymuszamy reload.
        const target = `/fleet-details?id=${notification.json.carId}${hid}`;
        if (this.$route.path === '/fleet-details') {
          window.location.href = target;
        } else {
          this.$router.push(target).catch((e) => { if (e.name !== 'NavigationDuplicated') throw e; });
        }
        return;
      }
      if (this.$route.path !== route) {
        this.$router.push(route).catch((e) => { if (e.name !== 'NavigationDuplicated') throw e; });
      }
    },
    getNotificationTitle(notification) {
      const type = notification.json?.type;
      if (!type) return notification.text || 'Powiadomienie';

      const titles = {
        'ADMIN_NEW_DRIVER_REGISTRATION': 'Nowa rejestracja',
        'ADMIN_DOCUMENTS_SUBMITTED': 'Dokumenty do weryfikacji',
        'ADMIN_NEW_TASK': 'Nowe zgłoszenie',
        'ADMIN_TASK_NOTE_FROM_DRIVER': 'Komentarz od kierowcy',
        'ADMIN_DRIVER_CHANGES_REQUEST': 'Prośba o zmianę danych',
        'ADMIN_INVOICE_SUBMITTED': 'Faktura do akceptacji',
        'ADMIN_B2B_INVOICE_SUBMITTED': 'Faktura B2B do akceptacji',
        'ADMIN_WEBSITE_LEAD': 'Zgłoszenie ze strony',
        'ADMIN_OWNER_REGISTRATION': 'Nowy właściciel aut',
        'ADMIN_OWNER_PROTOCOL': 'Protokół właściciela do akceptacji',
        'ADMIN_DRIVER_ZUS_CHANGE': 'Zmiana ZUS kierowcy',
        'ADMIN_IMPORT_ERROR': 'Błąd importu',
        'ADMIN_VEHICLE_INSURANCE_EXPIRING': 'Ubezpieczenie wygasa',
        'ADMIN_VEHICLE_INSPECTION_EXPIRING': 'Przegląd wygasa',
        'ADMIN_VEHICLE_SERVICE_DUE': 'Wymagany serwis',
        'ADMIN_DRIVER_SERVICE_SUBMITTED': 'Serwis do akceptacji',
        'ADMIN_CRASH_REPORTED': 'Nowa szkoda',
        'DRIVER_CHANGES_APPROVED': 'Zmiany zatwierdzone',
        'DRIVER_CHANGES_REJECTED': 'Zmiany odrzucone',
        'TASK_ASSIGNED': 'Przypisano zgłoszenie',
        'TASK_STATUS_CHANGED': 'Status zgłoszenia',
        'DOCUMENT_EXPIRING_30': 'Dokument wygasa (30 dni)',
        'DOCUMENT_EXPIRING_7': 'Dokument wygasa (7 dni)!',
        'DOCUMENT_EXPIRED': 'Dokument wygasł!',
      };
      return titles[type] || notification.text || 'Powiadomienie';
    },
  },
};
</script>

<style>
/* ===== Gesty przeglądarki na urządzeniach dotykowych =====
   Na tablecie przeciągnięcie od krawędzi lewo/prawo uruchamia nawigację
   wstecz/dalej przeglądarki, a pociągnięcie w dół — pull-to-refresh.
   W panelu (SPA z formularzami i polami podpisu) oba gesty przypadkowo
   wyrzucają z ekranu i kasują wpisywane dane, więc wyłączamy je globalnie.
   Zwykłe przewijanie i przyciski wstecz/dalej działają bez zmian. */
html,
body {
  overscroll-behavior-x: none;
  overscroll-behavior-y: contain;
}

/* ===== Obowiązkowy pop-up zadań ===== */
.btp-task-alert {
  width: min(620px, calc(100vw - 32px)) !important;
  max-height: calc(100vh - 32px) !important;
  padding: 0 !important;
  overflow: hidden !important;
  border: 1px solid rgba(104, 128, 255, 0.38) !important;
  border-radius: 22px !important;
  background:
    radial-gradient(circle at 88% 0%, rgba(66, 165, 245, 0.14), transparent 35%),
    linear-gradient(145deg, #172334 0%, #111a27 72%) !important;
  box-shadow: 0 28px 80px rgba(3, 8, 18, 0.62), 0 0 0 1px rgba(255, 255, 255, 0.03) inset !important;
  /* UWAGA: żadnej animacji na .swal2-popup — nadpisałaby animację zamykania
     (swal2-hide), przez co await Swal.fire nigdy by się nie rozwiązał i popup
     blokowałby stronę. */
}
.btp-task-alert::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 4px;
  background: linear-gradient(90deg, #5c6bc0, #42a5f5 55%, #00bcd4);
}
.btp-task-alert-title {
  margin: 0 !important;
  padding: 32px 32px 8px !important;
  color: #ffffff !important;
  font-size: 1.55rem !important;
  font-weight: 800 !important;
  line-height: 1.25 !important;
  letter-spacing: -0.025em !important;
  text-align: left !important;
}
.btp-task-alert-body {
  margin: 0 !important;
  padding: 12px 32px 26px !important;
  color: #ffffff !important;
  text-align: left !important;
}
.btp-task-alert__content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.btp-task-alert__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 18px;
}
.btp-task-alert__kind,
.btp-task-alert__counter {
  display: inline-flex;
  align-items: center;
  min-height: 30px;
  border-radius: 999px;
  font-size: 0.76rem;
  font-weight: 800;
  line-height: 1.2;
  letter-spacing: 0.055em;
  text-transform: uppercase;
}
.btp-task-alert__kind {
  gap: 7px;
  padding: 6px 11px;
}
.btp-task-alert__kind--new {
  border: 1px solid rgba(66, 165, 245, 0.38);
  background: rgba(66, 165, 245, 0.12);
  color: #90caf9;
}
.btp-task-alert__kind--due {
  border: 1px solid rgba(255, 183, 77, 0.42);
  background: rgba(255, 152, 0, 0.13);
  color: #ffcc80;
}
.btp-task-alert__kind-icon {
  font-size: 1rem;
  line-height: 1;
}
.btp-task-alert__counter {
  flex: 0 0 auto;
  padding: 6px 10px;
  border: 1px solid rgba(144, 164, 174, 0.2);
  background: rgba(255, 255, 255, 0.045);
  color: #90a4ae;
}
.btp-task-alert__task-title {
  color: #ffffff;
  font-size: 1.16rem;
  font-weight: 750;
  line-height: 1.4;
  overflow-wrap: anywhere;
}
.btp-task-alert__description {
  max-height: 180px;
  margin-top: 12px;
  padding: 14px 16px;
  overflow-y: auto;
  border: 1px solid rgba(144, 164, 174, 0.15);
  border-radius: 12px;
  background: rgba(4, 10, 19, 0.28);
  color: #c4d0da;
  font-size: 0.96rem;
  line-height: 1.6;
  overflow-wrap: anywhere;
  white-space: pre-line;
}
.btp-task-alert__deadline {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 16px;
  padding: 13px 15px;
  border: 1px solid rgba(255, 183, 77, 0.26);
  border-radius: 12px;
  background: rgba(255, 152, 0, 0.08);
}
.btp-task-alert__deadline-icon {
  flex: 0 0 auto;
  color: #ffb74d;
  font-size: 1.45rem;
}
.btp-task-alert__deadline-copy {
  display: flex;
  flex-direction: column;
  min-width: 0;
  gap: 2px;
}
.btp-task-alert__deadline-label {
  color: #90a4ae;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.055em;
  line-height: 1.2;
  text-transform: uppercase;
}
.btp-task-alert__deadline-value {
  color: #ffffff;
  font-size: 0.96rem;
  line-height: 1.35;
}
.btp-task-alert-actions {
  display: grid !important;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 12px !important;
  width: 100% !important;
  margin: 0 !important;
  padding: 0 32px 32px !important;
}
.btp-task-alert-btn,
.btp-task-alert-snooze {
  min-height: 48px !important;
  margin: 0 !important;
  border-radius: 12px !important;
  font-weight: 800 !important;
  font-size: 0.94rem !important;
  line-height: 1.2 !important;
  letter-spacing: 0 !important;
  text-transform: none !important;
  transition: background-color 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease !important;
}
.btp-task-alert-btn {
  padding: 12px 24px !important;
  border: 1px solid rgba(119, 147, 255, 0.5) !important;
  background: linear-gradient(135deg, #3f51b5, #5267d8) !important;
  color: #ffffff !important;
  box-shadow: 0 10px 24px rgba(63, 81, 181, 0.28) !important;
}
.btp-task-alert-btn:hover {
  background: linear-gradient(135deg, #4c5fc7, #6175e5) !important;
  box-shadow: 0 12px 28px rgba(63, 81, 181, 0.38) !important;
}
.btp-task-alert-snooze {
  padding: 12px 18px !important;
  border: 1px solid rgba(144, 164, 174, 0.28) !important;
  background: rgba(255, 255, 255, 0.045) !important;
  color: #c4d0da !important;
  box-shadow: none !important;
}
.btp-task-alert-snooze:hover {
  border-color: rgba(144, 164, 174, 0.48) !important;
  background: rgba(255, 255, 255, 0.08) !important;
}
.btp-task-alert-btn:focus,
.btp-task-alert-snooze:focus {
  outline: 2px solid #90caf9 !important;
  outline-offset: 2px !important;
}

@media (max-width: 600px) {
  .btp-task-alert {
    width: calc(100vw - 20px) !important;
    max-height: calc(100vh - 20px) !important;
    border-radius: 17px !important;
  }
  .btp-task-alert-title {
    padding: 25px 20px 7px !important;
    font-size: 1.3rem !important;
  }
  .btp-task-alert-body {
    padding: 10px 20px 21px !important;
  }
  .btp-task-alert__meta {
    align-items: flex-start;
    margin-bottom: 15px;
  }
  .btp-task-alert__kind,
  .btp-task-alert__counter {
    min-height: 28px;
    font-size: 0.68rem;
  }
  .btp-task-alert__task-title {
    font-size: 1.06rem;
  }
  .btp-task-alert__description {
    max-height: 24vh;
    padding: 12px 13px;
    font-size: 0.9rem;
  }
  .btp-task-alert__deadline {
    padding: 12px 13px;
  }
  .btp-task-alert-actions {
    grid-template-columns: 1fr;
    padding: 0 20px 20px !important;
  }
  .btp-task-alert-btn,
  .btp-task-alert-snooze {
    width: 100% !important;
  }
}

/* ===== BTP Design System — Dark Mode ===== */

:root {
  --bg-app: #0F1923;
  --bg-surface: #1A2433;
  --bg-surface-raised: #212E3F;
  --bg-surface-hover: #2A3A50;
  --bg-input: #1E2D3F;
  --bg-topbar: #3F51B5;
  --accent-primary: #3F51B5;
  --accent-primary-hover: #5C6BC0;
  --accent-orange: #FF9800;
  --accent-red: #F44336;
  --accent-cyan: #00BCD4;
  --accent-blue: #42A5F5;
  --accent-green: #4CAF50;
  --text-primary: #FFFFFF;
  --text-secondary: #90A4AE;
  --text-muted: #546E7A;
  --text-accent: #42A5F5;
  --border-default: #2A3A50;
  --border-input: #37474F;
  --border-input-focus: #3F51B5;
  --radius-card: 8px;
  --radius-btn: 4px;
  --radius-input: 4px;
  --shadow-card: 0 2px 8px rgba(0,0,0,0.4);
  --shadow-topbar: 0 2px 4px rgba(0,0,0,0.5);
  --shadow-dropdown: 0 4px 16px rgba(0,0,0,0.6);
}

/* App background */
.v-application.btp-app {
  background-color: var(--bg-app) !important;
}
.theme--dark.v-application {
  background: var(--bg-app) !important;
}

/* ===== Topbar ===== */
.btp-topbar.v-app-bar {
  background-color: var(--bg-topbar) !important;
  box-shadow: var(--shadow-topbar) !important;
}

/* ===== Sidebar — driver-app dark === */
.btp-sidebar.v-navigation-drawer {
  background-color: #181C28 !important;
  border-right: 1px solid rgba(255, 255, 255, 0.04) !important;
}
.btp-sidebar-logo {
  height: 72px;
  background: linear-gradient(135deg, #1F2434 0%, #232838 100%);
  overflow: hidden;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.btp-logo-img {
  max-height: 56px;
  max-width: 200px;
  object-fit: contain;
  display: block;
}
.btp-logo-img.mini {
  max-height: 44px;
  max-width: 48px;
}
.btp-nav-list {
  padding: 10px 0 !important;
}
.btp-nav-item {
  height: 42px !important;
  margin: 2px 8px !important;
  border-radius: 12px !important;
  transition: background 0.15s ease, transform 0.1s ease;
}
.btp-nav-item .v-list-item__icon {
  margin-right: 12px !important;
}
.btp-nav-item .v-icon {
  color: #B0BEC5 !important;
  transition: color 0.15s ease;
}
.btp-nav-text {
  font-size: 13px !important;
  font-weight: 600 !important;
  color: #B0BEC5 !important;
  letter-spacing: -0.01em !important;
  transition: color 0.15s ease;
}
.btp-nav-item:hover {
  background-color: rgba(255, 255, 255, 0.04) !important;
}
.btp-nav-item:hover .v-icon,
.btp-nav-item:hover .btp-nav-text {
  color: #fff !important;
}
.btp-nav-active {
  background: linear-gradient(135deg, rgba(41, 98, 255, 0.22), rgba(84, 120, 255, 0.16)) !important;
  border-left: none !important;
  box-shadow: 0 4px 14px rgba(41, 98, 255, 0.18);
}
.btp-nav-active::before {
  content: '';
  position: absolute;
  left: -8px;
  top: 8px;
  bottom: 8px;
  width: 3px;
  background: #5478FF;
  border-radius: 0 3px 3px 0;
}
.btp-nav-active .v-icon {
  color: #5478FF !important;
}
.btp-nav-active .btp-nav-text {
  color: #fff !important;
  font-weight: 700 !important;
}

/* ===== Nav Groups ===== */
.btp-nav-group .v-list-group__header {
  height: 40px !important;
  margin: 2px 8px !important;
  border-radius: 12px !important;
}
.btp-nav-group .v-list-group__header .v-icon {
  color: #B0BEC5 !important;
}
.btp-nav-group .v-list-group__header:hover {
  background-color: rgba(255, 255, 255, 0.04) !important;
}
.btp-nav-group .v-list-group__header:hover .v-icon,
.btp-nav-group .v-list-group__header:hover .btp-nav-text {
  color: #fff !important;
}
.btp-nav-group .v-list-group--active > .v-list-group__header .v-icon {
  color: #5478FF !important;
}
.btp-nav-group .v-list-group--active > .v-list-group__header .btp-nav-text {
  color: #fff !important;
  font-weight: 700 !important;
}
.btp-nav-subitem {
  padding-left: 24px !important;
  height: 38px !important;
  background: rgba(255, 255, 255, 0.02);
}
.btp-nav-group .v-list-group__items {
  background-color: transparent !important;
  margin: 4px 4px 6px;
  border-left: 2px solid rgba(255, 255, 255, 0.04);
  padding-left: 4px;
}

/* Search field in sidebar */
.btp-nav-search >>> .v-input__slot,
.btp-nav-search .v-input__slot {
  background: #232838 !important;
  border-radius: 12px !important;
  min-height: 40px !important;
}
.btp-nav-search fieldset {
  border-color: rgba(255, 255, 255, 0.08) !important;
}
.btp-nav-search .v-label,
.btp-nav-search input {
  color: #B0BEC5 !important;
  font-size: 13px !important;
}
.btp-nav-search .v-input__prepend-inner .v-icon {
  color: #6E7691 !important;
}

/* ===== Drag & Drop Config ===== */
.btp-draggable-chip {
  cursor: grab !important;
  transition: opacity 0.15s, transform 0.15s;
}
.btp-draggable-chip:active {
  cursor: grabbing !important;
}
.btp-config-group-card {
  transition: border-color 0.2s, box-shadow 0.2s;
}
.btp-config-group-card:hover {
  border-color: var(--accent-primary) !important;
}
.btp-drag-over {
  border-color: var(--accent-primary) !important;
  box-shadow: 0 0 0 2px rgba(63,81,181,0.3) !important;
  background-color: rgba(63,81,181,0.05) !important;
}
.btp-dragging {
  opacity: 0.4 !important;
}
.btp-page-drag-left {
  border-left: 3px solid var(--accent-primary) !important;
  margin-left: 0 !important;
  padding-left: 8px !important;
}
.btp-insert-indicator {
  display: inline-block;
  width: 3px;
  height: 24px;
  background-color: var(--accent-primary);
  border-radius: 2px;
  vertical-align: middle;
}
.btp-end-drop-zone {
  vertical-align: middle;
  display: inline-flex !important;
  align-items: center;
}

.btp-logout-item {
  border-radius: 12px !important;
  margin: 6px 8px 4px !important;
  height: 42px !important;
  background: rgba(244, 67, 54, 0.08) !important;
  border: 1px solid rgba(244, 67, 54, 0.2) !important;
  transition: background 0.15s ease;
}
.btp-logout-item:hover {
  background-color: rgba(244, 67, 54, 0.18) !important;
  border-color: rgba(244, 67, 54, 0.4) !important;
}
.btp-logout-item .v-icon { color: #FF8A80 !important; }
.btp-logout-item .red--text { color: #FF8A80 !important; }

/* ===== Main Content ===== */
.btp-main {
  background-color: var(--bg-app) !important;
}
.imp-banner {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: linear-gradient(90deg, #C2410C, #EA580C);
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}
.btp-main .v-main__wrap {
  padding: 24px;
}

/* ===== Cards ===== */
.theme--dark.v-card,
.theme--dark.v-sheet {
  background-color: var(--bg-surface) !important;
  border-color: var(--border-default) !important;
}
.theme--dark.v-card.v-card--outlined {
  border: 1px solid var(--border-default) !important;
  border-radius: var(--radius-card) !important;
  box-shadow: var(--shadow-card) !important;
}

/* ===== Data Tables ===== */
/* Spójne, zaokrąglone tabele na wszystkich stronach.
   `overflow: hidden` jest konieczny — bez niego wewnętrzny .v-data-table__wrapper
   pokrywa rogi i border-radius jest niewidoczny. */
.v-data-table {
  border-radius: 12px !important;
  overflow: hidden !important;
  border: 1px solid var(--border-default);
}
.theme--dark.v-data-table {
  background-color: var(--bg-surface) !important;
  border-radius: 12px !important;
}
.theme--dark.v-data-table > .v-data-table__wrapper > table > thead > tr > th {
  background-color: var(--bg-surface) !important;
  color: var(--text-secondary) !important;
  font-size: 12px !important;
  font-weight: 600 !important;
  border-bottom: 1px solid var(--border-default) !important;
}
.theme--dark.v-data-table > .v-data-table__wrapper > table > tbody > tr {
  background-color: var(--bg-surface-raised) !important;
}
.theme--dark.v-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
  background-color: var(--bg-surface-hover) !important;
}
.theme--dark.v-data-table > .v-data-table__wrapper > table > tbody > tr > td {
  color: var(--text-primary) !important;
  font-size: 14px !important;
  border-bottom: 1px solid var(--border-default) !important;
  padding: 12px 16px !important;
}

/* ===== Text Fields & Inputs ===== */
.theme--dark.v-text-field--outlined > .v-input__control > .v-input__slot {
  background-color: var(--bg-input) !important;
}
.theme--dark.v-text-field--outlined fieldset {
  border-color: var(--border-input) !important;
}
.theme--dark.v-text-field--outlined.v-input--is-focused fieldset {
  border-color: var(--border-input-focus) !important;
  border-width: 2px !important;
}
.theme--dark.v-text-field > .v-input__control > .v-input__slot {
  background-color: var(--bg-input) !important;
}
.theme--dark.v-label {
  color: var(--text-secondary) !important;
  font-size: 12px !important;
}
.theme--dark.v-input input,
.theme--dark.v-input textarea {
  color: var(--text-primary) !important;
}
.theme--dark.v-input input::placeholder {
  color: var(--text-muted) !important;
}

/* ===== Select / Dropdown ===== */
.theme--dark.v-select .v-select__selections {
  color: var(--text-primary) !important;
}
.theme--dark.v-menu__content {
  background-color: var(--bg-input) !important;
  box-shadow: var(--shadow-dropdown) !important;
  border-radius: var(--radius-btn) !important;
}
.theme--dark.v-list {
  background-color: var(--bg-input) !important;
}
.theme--dark.v-list-item:hover {
  background-color: var(--bg-surface-hover) !important;
}

/* ===== Buttons ===== */
.theme--dark.v-btn.primary,
.v-btn.v-btn--has-bg.primary {
  background-color: var(--accent-primary) !important;
  border-radius: var(--radius-btn) !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: uppercase !important;
}
.theme--dark.v-btn.primary:hover {
  background-color: var(--accent-primary-hover) !important;
}

/* ===== Pagination ===== */
.theme--dark.v-data-footer {
  border-top: 1px solid var(--border-default) !important;
  color: var(--text-secondary) !important;
}
.theme--dark.v-data-footer .v-select {
  color: var(--text-secondary) !important;
}

/* ===== Chips / Badges ===== */
.theme--dark.v-chip {
  border-radius: var(--radius-btn) !important;
  font-size: 11px !important;
  font-weight: 600 !important;
}

/* ===== Expansion Panels / Accordion ===== */
.theme--dark.v-expansion-panels .v-expansion-panel {
  background-color: var(--bg-surface) !important;
  border-bottom: 1px solid var(--border-default) !important;
}
.theme--dark.v-expansion-panel-header {
  color: var(--text-primary) !important;
  padding: 16px !important;
}

/* ===== Dialogs ===== */
.theme--dark.v-dialog .v-card {
  background-color: var(--bg-surface) !important;
}

/* ===== Tabs ===== */
.theme--dark.v-tabs > .v-tabs-bar {
  background-color: var(--bg-surface) !important;
}

/* ===== Notifications ===== */
/* === Powiadomienia w stylu iOS-blur / Stitch === */
.btp-notification-card {
  background: rgba(31, 31, 40, 0.85) !important;
  backdrop-filter: blur(25px) saturate(180%);
  -webkit-backdrop-filter: blur(25px) saturate(180%);
  border: 0.5px solid rgba(255, 255, 255, 0.12) !important;
  border-radius: 20px !important;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5), 0 0 0 1px rgba(192, 193, 255, 0.08) inset !important;
  color: #e3e1ed !important;
}
.btp-notification-card .v-card__title {
  font-size: 15px !important;
  font-weight: 700 !important;
  letter-spacing: -0.01em !important;
  color: #e3e1ed !important;
  padding: 16px 18px !important;
}
.btp-notification-card .v-card__title .v-btn {
  font-size: 11px !important;
  font-weight: 600 !important;
  letter-spacing: 0.04em !important;
  text-transform: uppercase !important;
  color: #c0c1ff !important;
}
.btp-notification-list {
  background: transparent !important;
  padding: 4px 0 !important;
}
.btp-notification-list .v-list-item {
  min-height: 64px !important;
  padding: 12px 18px !important;
  border-left: 3px solid transparent;
  transition: background-color .15s ease, border-color .15s ease;
}
.btp-notification-list .v-list-item:hover {
  background-color: rgba(192, 193, 255, 0.06) !important;
}
.btp-notification-list .v-list-item__icon {
  margin-right: 12px !important;
  align-self: center !important;
}
.btp-notification-list .v-list-item__title {
  font-size: 14px !important;
  font-weight: 600 !important;
  letter-spacing: -0.01em !important;
  line-height: 1.4 !important;
  color: #e3e1ed !important;
  white-space: normal !important;
}
.btp-notification-list .v-list-item__subtitle {
  font-size: 13px !important;
  font-weight: 400 !important;
  line-height: 1.45 !important;
  color: rgba(227, 225, 237, 0.7) !important;
  white-space: normal !important;
  margin-top: 2px !important;
}
.btp-notification-list .v-list-item__subtitle.caption {
  font-size: 11px !important;
  color: rgba(227, 225, 237, 0.5) !important;
  letter-spacing: 0.02em !important;
  margin-top: 4px !important;
}
.btp-notification-list .v-chip {
  font-size: 10.5px !important;
  font-weight: 700 !important;
  letter-spacing: 0.05em !important;
  text-transform: uppercase !important;
}
.btp-unread {
  background-color: rgba(192, 193, 255, 0.08) !important;
  border-left-color: #c0c1ff !important;
}
.btp-unread .v-list-item__title {
  font-weight: 700 !important;
}

/* ===== Snackbar (toast) — iOS-blur Stitch style ===== */
.btp-snackbar .v-snack__wrapper {
  border-radius: 20px !important;
  background: rgba(31, 31, 40, 0.85) !important;
  backdrop-filter: blur(25px) saturate(180%);
  -webkit-backdrop-filter: blur(25px) saturate(180%);
  border: 0.5px solid rgba(255, 255, 255, 0.15) !important;
  box-shadow: 0 20px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(192, 193, 255, 0.1) inset !important;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  -webkit-font-smoothing: antialiased;
  min-width: 380px;
  max-width: 560px;
  margin-top: 24px !important;
  color: #e3e1ed !important;
}
.btp-snackbar.success .v-snack__wrapper {
  border-color: rgba(95, 100, 249, 0.35) !important;
  box-shadow: 0 20px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(95, 100, 249, 0.15) inset !important;
}
.btp-snackbar.error .v-snack__wrapper {
  border-color: rgba(255, 180, 171, 0.35) !important;
  box-shadow: 0 20px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(255, 180, 171, 0.15) inset !important;
}
.btp-snackbar .v-snack__content {
  padding: 18px 22px !important;
  color: #e3e1ed !important;
}
.btp-snackbar .btp-snackbar-text {
  font-size: 15px !important;
  font-weight: 600 !important;
  letter-spacing: -0.01em !important;
  line-height: 1.45 !important;
  color: #e3e1ed !important;
}
.btp-snackbar .v-icon {
  font-size: 22px !important;
  margin-right: 8px !important;
}

/* ===== SweetAlert2 (Swal.fire) — iOS-blur Stitch style ===== */
.swal2-popup {
  border-radius: 20px !important;
  background: rgba(31, 31, 40, 0.92) !important;
  backdrop-filter: blur(25px) saturate(180%);
  -webkit-backdrop-filter: blur(25px) saturate(180%);
  border: 0.5px solid rgba(255, 255, 255, 0.15) !important;
  box-shadow: 0 20px 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(192, 193, 255, 0.1) inset !important;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important;
  color: #e3e1ed !important;
}
.swal2-title {
  color: #e3e1ed !important;
  font-weight: 700 !important;
  letter-spacing: -0.01em !important;
  font-size: 19px !important;
}
.swal2-html-container {
  color: rgba(227, 225, 237, 0.85) !important;
  font-size: 14px !important;
  line-height: 1.5 !important;
}
.swal2-actions {
  gap: 8px !important;
}
.swal2-styled.swal2-confirm {
  background: #5f64f9 !important;
  color: #ffffff !important;
  border-radius: 12px !important;
  box-shadow: 0 4px 12px rgba(95, 100, 249, 0.35) !important;
  font-weight: 600 !important;
  letter-spacing: -0.01em !important;
}
.swal2-styled.swal2-cancel {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #e3e1ed !important;
  border-radius: 12px !important;
  border: 0.5px solid rgba(255, 255, 255, 0.15) !important;
  font-weight: 500 !important;
}
.swal2-styled.swal2-deny {
  background: rgba(255, 180, 171, 0.15) !important;
  color: #ffb4ab !important;
  border-radius: 12px !important;
  border: 0.5px solid rgba(255, 180, 171, 0.3) !important;
  font-weight: 600 !important;
}
.swal2-icon.swal2-success .swal2-success-ring {
  border-color: rgba(95, 100, 249, 0.35) !important;
}
.swal2-icon.swal2-success [class^='swal2-success-line'] {
  background-color: #5f64f9 !important;
}
.swal2-icon.swal2-error {
  border-color: rgba(255, 180, 171, 0.5) !important;
  color: #ffb4ab !important;
}
.swal2-icon.swal2-error [class^='swal2-x-mark-line'] {
  background-color: #ffb4ab !important;
}
.swal2-icon.swal2-warning {
  border-color: rgba(255, 181, 153, 0.5) !important;
  color: #ffb599 !important;
}
.swal2-icon.swal2-info {
  border-color: rgba(192, 193, 255, 0.5) !important;
  color: #c0c1ff !important;
}
.swal2-icon.swal2-question {
  border-color: rgba(192, 193, 255, 0.5) !important;
  color: #c0c1ff !important;
}
.swal2-backdrop-show {
  background: rgba(11, 19, 38, 0.6) !important;
  backdrop-filter: blur(4px) !important;
  -webkit-backdrop-filter: blur(4px) !important;
}
.swal2-input, .swal2-textarea {
  background: rgba(255, 255, 255, 0.06) !important;
  color: #e3e1ed !important;
  border: 0.5px solid rgba(255, 255, 255, 0.15) !important;
  border-radius: 12px !important;
}

/* ===== Disabled state ===== */
.theme--dark.v-btn--disabled {
  opacity: 0.4 !important;
}

/* ===== Scrollbar ===== */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}
::-webkit-scrollbar-track {
  background: var(--bg-app);
}
::-webkit-scrollbar-thumb {
  background: var(--border-default);
  border-radius: 3px;
}
::-webkit-scrollbar-thumb:hover {
  background: var(--text-muted);
}

/* ===== Dividers ===== */
.theme--dark.v-divider {
  border-color: var(--border-default) !important;
}

/* ===== Date Picker ===== */
.theme--dark.v-picker {
  background-color: var(--bg-surface) !important;
}
.theme--dark.v-date-picker-header {
  background-color: var(--bg-surface) !important;
}
.theme--dark.v-date-picker-table {
  background-color: var(--bg-surface) !important;
}

/* ===== Toolbar ===== */
.theme--dark.v-toolbar {
  background-color: var(--bg-surface) !important;
}

/* ===== Page Titles ===== */
.v-card__title {
  font-size: 18px !important;
  font-weight: 600 !important;
  color: var(--text-primary) !important;
}

/* ===== SweetAlert2 Dark ===== */
.swal2-popup {
  background-color: var(--bg-surface) !important;
  color: var(--text-primary) !important;
}
.swal2-title {
  color: var(--text-primary) !important;
}
.swal2-html-container {
  color: var(--text-secondary) !important;
}

/* ===== Shared utility classes — wzorzec z driver app, używany na nowych
   modernizowanych stronach admina (DashboardPage, PayoutsPage itd.) ===== */

/* Section header — uppercase muted label nad kartami sekcji */
.btp-section-title {
  padding: 14px 4px 6px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 8px;
}
.btp-section-title-sm {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: 6px;
  margin-top: 4px;
}

/* Count pill — siwy chip z liczbą obok section-title */
.btp-count-pill {
  background: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 10px;
  color: var(--text-secondary);
  letter-spacing: 0.04em;
}

/* Info pill — zastępstwo dla v-alert outlined */
.btp-info-pill {
  background: rgba(66, 165, 245, 0.1);
  border: 1px solid rgba(66, 165, 245, 0.3);
  border-radius: 10px;
  padding: 10px 14px;
  font-size: 13px;
  color: var(--text-secondary);
  display: flex;
  align-items: flex-start;
  gap: 6px;
  line-height: 1.45;
}
.btp-info-pill--success {
  background: rgba(76, 175, 80, 0.1);
  border-color: rgba(76, 175, 80, 0.3);
  color: #C5E1A5;
}
.btp-info-pill--warning {
  background: rgba(255, 152, 0, 0.1);
  border-color: rgba(255, 152, 0, 0.3);
  color: #FFD180;
}
.btp-info-pill--error {
  background: rgba(244, 67, 54, 0.1);
  border-color: rgba(244, 67, 54, 0.3);
  color: #FFB4AB;
}

/* Badge pills — zamiast v-chip */
.btp-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
}
.btp-badge--success { background: rgba(76, 175, 80, 0.15); color: #81C784; }
.btp-badge--warning { background: rgba(255, 152, 0, 0.15); color: #FFB74D; }
.btp-badge--error   { background: rgba(244, 67, 54, 0.15); color: #EF9A9A; }
.btp-badge--info    { background: rgba(66, 165, 245, 0.15); color: #64B5F6; }

/* Surface card — base + raised variants korzystające z design tokens */
.btp-card {
  background: var(--bg-surface) !important;
  border: 1px solid var(--border-default) !important;
  border-radius: 12px !important;
  box-shadow: none !important;
}
.btp-card--raised {
  background: var(--bg-surface-raised) !important;
}

/* ===== Global dark-theme overrides for modernized admin pages =====
   Default (underlined) v-text-field used to render with white background
   and no visible bottom border on dark cards. These rules fix the
   underline color, input text color, and label color globally for
   any descendant of a modernized page wrapper. */
.admins-page .v-input__slot::before,
.audit-logs-page .v-input__slot::before,
.bolt-sync-page .v-input__slot::before,
.bonus-page .v-input__slot::before,
.car-crash-details-page .v-input__slot::before,
.car-crash-page .v-input__slot::before,
.company-page .v-input__slot::before,
.cost-invoices-page .v-input__slot::before,
.driver-fleet-page .v-input__slot::before,
.driver-onboarding-page .v-input__slot::before,
.incomes-page .v-input__slot::before,
.invoices-page .v-input__slot::before,
.payout-config-page .v-input__slot::before,
.pending-changes-page .v-input__slot::before,
.pending-documents-page .v-input__slot::before,
.points-settings-page .v-input__slot::before,
.sms-page .v-input__slot::before,
.sub-fleet-page .v-input__slot::before,
.system-settings-page .v-input__slot::before,
.tasks-page .v-input__slot::before,
.uploader-page .v-input__slot::before,
.uploader-templates-page .v-input__slot::before {
  border-color: #2A3142 !important;
}
.admins-page .v-input--is-focused .v-input__slot::after,
.audit-logs-page .v-input--is-focused .v-input__slot::after,
.bolt-sync-page .v-input--is-focused .v-input__slot::after,
.bonus-page .v-input--is-focused .v-input__slot::after,
.car-crash-details-page .v-input--is-focused .v-input__slot::after,
.car-crash-page .v-input--is-focused .v-input__slot::after,
.company-page .v-input--is-focused .v-input__slot::after,
.cost-invoices-page .v-input--is-focused .v-input__slot::after,
.driver-fleet-page .v-input--is-focused .v-input__slot::after,
.driver-onboarding-page .v-input--is-focused .v-input__slot::after,
.incomes-page .v-input--is-focused .v-input__slot::after,
.invoices-page .v-input--is-focused .v-input__slot::after,
.payout-config-page .v-input--is-focused .v-input__slot::after,
.pending-changes-page .v-input--is-focused .v-input__slot::after,
.pending-documents-page .v-input--is-focused .v-input__slot::after,
.points-settings-page .v-input--is-focused .v-input__slot::after,
.sms-page .v-input--is-focused .v-input__slot::after,
.sub-fleet-page .v-input--is-focused .v-input__slot::after,
.system-settings-page .v-input--is-focused .v-input__slot::after,
.tasks-page .v-input--is-focused .v-input__slot::after,
.uploader-page .v-input--is-focused .v-input__slot::after,
.uploader-templates-page .v-input--is-focused .v-input__slot::after {
  border-color: #5478FF !important;
}
[class$="-page"][class*="bonus"] input,
[class$="-page"][class*="incomes"] input,
[class$="-page"][class*="invoices"] input,
[class$="-page"][class*="cost-invoices"] input,
[class$="-page"][class*="tasks"] input,
[class$="-page"][class*="company"] input,
[class$="-page"][class*="admins"] input,
[class$="-page"][class*="driver-fleet"] input,
[class$="-page"][class*="pending-changes"] input,
[class$="-page"][class*="pending-documents"] input,
[class$="-page"][class*="points-settings"] input,
[class$="-page"][class*="sub-fleet"] input,
[class$="-page"][class*="bolt-sync"] input,
[class$="-page"][class*="payout-config"] input,
[class$="-page"][class*="car-crash"] input,
[class$="-page"][class*="audit-logs"] input,
[class$="-page"][class*="sms"] input,
[class$="-page"][class*="system-settings"] input,
[class$="-page"][class*="uploader"] input,
[class$="-page"][class*="driver-onboarding"] input {
  color: #E6EAF2 !important;
}

/* ===== Menu config dialog (sidebar settings) ===== */
.menu-config-dialog .v-card {
  background: #181C28 !important;
  color: #E6EAF2 !important;
}
.menu-config-dialog .v-card__title {
  color: #FFFFFF !important;
  background: #181C28 !important;
  border-bottom: 1px solid #2A3142;
}
.menu-config-dialog .v-card__text {
  background: #181C28 !important;
}
.menu-config-dialog .v-card__actions {
  background: #181C28 !important;
  border-top: 1px solid #2A3142;
}
.menu-config-dialog .v-divider {
  border-color: #2A3142 !important;
}
.menu-config-dialog .v-card.v-sheet--outlined,
.menu-config-dialog .btp-config-group-card {
  background: #232838 !important;
  border-color: #2A3142 !important;
  border-radius: 12px !important;
}
.menu-config-dialog .v-card.v-sheet--outlined .v-card__title,
.menu-config-dialog .btp-config-group-card .v-card__title {
  background: transparent !important;
  color: #E6EAF2 !important;
  border-bottom: none !important;
}
.menu-config-dialog .text--secondary {
  color: #94A0B8 !important;
}
.menu-config-dialog .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.menu-config-dialog .v-text-field input,
.menu-config-dialog .v-text-field textarea,
.menu-config-dialog .v-select__selection {
  color: #E6EAF2 !important;
}
.menu-config-dialog .v-label {
  color: #94A0B8 !important;
}
.menu-config-dialog .v-input__slot::before {
  border-color: #2A3142 !important;
}
.menu-config-dialog .v-input--is-focused .v-input__slot::after {
  border-color: #5478FF !important;
}
.menu-config-dialog .v-chip:not(.v-chip--active) {
  background: #1F2433 !important;
  color: #E6EAF2 !important;
  border: 1px solid #2A3142 !important;
}
.menu-config-dialog .v-chip .v-icon {
  color: #94A0B8 !important;
}
.menu-config-dialog .btp-config-group-card:hover {
  border-color: #5478FF !important;
}
.menu-config-dialog .btp-drag-over {
  border-color: #5478FF !important;
  box-shadow: 0 0 0 2px rgba(84, 120, 255, 0.25) !important;
  background-color: rgba(84, 120, 255, 0.06) !important;
}
.menu-config-dialog .btp-insert-indicator {
  background-color: #5478FF !important;
}

/* ===== Generic dark form dialog (add via content-class="dark-form-dialog") ===== */
.dark-form-dialog .v-card {
  background: #232838 !important;
  color: #E6EAF2 !important;
  border-radius: 16px !important;
  border: 1px solid #2A3142 !important;
}
.dark-form-dialog .v-card__title {
  color: #FFFFFF !important;
  background: #1F2433 !important;
  border-bottom: 1px solid #2A3142;
  padding: 18px 24px !important;
}
.dark-form-dialog .v-card__text {
  background: #232838 !important;
  color: #E6EAF2 !important;
  padding-top: 20px !important;
}
.dark-form-dialog .v-card__actions {
  background: #1F2433 !important;
  border-top: 1px solid #2A3142;
  padding: 12px 20px !important;
}
.dark-form-dialog .v-divider {
  border-color: #2A3142 !important;
}
.dark-form-dialog .v-text-field--outlined fieldset {
  border-color: #2A3142 !important;
}
.dark-form-dialog .v-text-field--outlined:hover fieldset {
  border-color: #3A4258 !important;
}
.dark-form-dialog .v-text-field--outlined.v-input--is-focused fieldset {
  border-color: #5478FF !important;
}
.dark-form-dialog .v-text-field input,
.dark-form-dialog .v-text-field textarea,
.dark-form-dialog .v-select__selection,
.dark-form-dialog .v-autocomplete input {
  color: #E6EAF2 !important;
}
.dark-form-dialog .v-label {
  color: #94A0B8 !important;
}
.dark-form-dialog .v-label.v-label--active {
  color: #5478FF !important;
}
.dark-form-dialog .v-input__slot::before {
  border-color: #2A3142 !important;
}
.dark-form-dialog .v-input--is-focused .v-input__slot::after {
  border-color: #5478FF !important;
}
.dark-form-dialog .grey--text,
.dark-form-dialog .text--secondary {
  color: #94A0B8 !important;
}
.dark-form-dialog .v-input--switch label {
  color: #E6EAF2 !important;
}
.dark-form-dialog .v-input--switch .v-input__slot {
  background: transparent !important;
}
.dark-form-dialog .v-list,
.dark-form-dialog .v-list-item {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.dark-form-dialog .v-list-item__title {
  color: #E6EAF2 !important;
}
.dark-form-dialog .v-card.v-sheet--outlined {
  background: #1F2433 !important;
  border-color: #2A3142 !important;
}
.dark-form-dialog .v-input--checkbox label,
.dark-form-dialog .v-input--radio label {
  color: #E6EAF2 !important;
}
.dark-form-dialog input::placeholder,
.dark-form-dialog textarea::placeholder {
  color: #6B7494 !important;
}
.dark-form-dialog .v-messages__message {
  color: #94A0B8 !important;
}
.dark-form-dialog .error--text,
.dark-form-dialog .error--text .v-messages__message {
  color: #FF6B6B !important;
}

/* ===== Dark date-picker popup (add via content-class="dark-date-picker") ===== */
.dark-date-picker .v-date-picker-table,
.dark-date-picker .v-picker,
.dark-date-picker .v-picker__body,
.dark-date-picker .v-date-picker-header {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.dark-date-picker .v-date-picker-table th {
  color: #94A0B8 !important;
}
.dark-date-picker .v-date-picker-table td button.v-btn {
  color: #E6EAF2 !important;
}
.dark-date-picker .v-date-picker-table td button.v-btn:hover {
  background: rgba(84, 120, 255, 0.12) !important;
}
.dark-date-picker .v-date-picker-table .v-btn--active {
  background: #5478FF !important;
  color: #FFFFFF !important;
}
.dark-date-picker .v-date-picker-table .v-btn--outlined {
  border-color: #5478FF !important;
}
.dark-date-picker .v-date-picker-header__value button {
  color: #E6EAF2 !important;
}
.dark-date-picker .v-date-picker-header .v-btn .v-icon {
  color: #94A0B8 !important;
}
.dark-date-picker .v-date-picker-table .v-btn--text:not(.v-btn--active) {
  color: #94A0B8 !important;
}
.dark-date-picker .v-date-picker-years,
.dark-date-picker .v-date-picker-months {
  background: #232838 !important;
  color: #E6EAF2 !important;
}
.dark-date-picker .v-date-picker-years li:hover,
.dark-date-picker .v-date-picker-months button:hover {
  background: rgba(84, 120, 255, 0.12) !important;
}
.dark-date-picker .v-date-picker-years .active,
.dark-date-picker .v-date-picker-months .v-btn--active {
  color: #5478FF !important;
  font-weight: 700 !important;
}

</style>
